import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { DataModel, Dimension, Point } from "./AppTypes";
import { getLessonBg, makeThemeVars, Mode } from "./colorUtils";
import { notifyProjectActivity } from "./api/pingClient";
import { ProjectDetail, UserInfo } from "./model/backendTypes";
import ProfileDropdown from "./ProfileDropdown";
import ProjectOpenDropdown from "./ProjectOpenDropdown";
import { buildTimeSlotLayout, formatMinutes, parseHHMM } from "./planTimeLayout";
import type { TimeSlotOverflowGroup, TimeSlotSegment } from "./planTimeLayout";

function setDetachedDragImage(event: React.DragEvent<HTMLElement>) {
  const source = event.target instanceof Element
    ? event.target.closest("[draggable='true']")
    : null;
  if (!(source instanceof HTMLElement)) return;

  const clone = source.cloneNode(true) as HTMLElement;
  const rect = source.getBoundingClientRect();

  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  clone.style.boxSizing = "border-box";

  if (source instanceof HTMLTableCellElement) {
    clone.style.display = "flex";
    clone.style.alignItems = "center";
    clone.style.justifyContent = "center";
  }

  clone.style.transform = "none";
  clone.style.position = "fixed";
  clone.style.top = "0px";
  clone.style.left = "0px";
  clone.style.zIndex = "-9999";
  clone.style.pointerEvents = "none";
  clone.classList.remove("is-plan-hovered");
  document.body.appendChild(clone);

  event.dataTransfer.setDragImage(
    clone,
    Math.max(0, event.clientX - rect.left),
    Math.max(0, event.clientY - rect.top)
  );

  window.setTimeout(() => clone.remove(), 100);
}

type SuperFilterKind = "point" | "tag";
type SuperFilterItem = { kind: SuperFilterKind; id: string };
type PlanRow = Record<string, string>;
type AxisTagSpan = { start: number; count: number };
type AxisTagLane = { tagId: string; tagName: string; spans: AxisTagSpan[] };
type TimeSlotPointLabel = { point: Point; startMin: number; endMin: number };

const PLAN_HOVER_SELECTOR = [
  ".clickable:not(.selected):not(.associated):not(.superfilter-selected)",
  ".lesson-cell",
  ".cell-pager-arrow",
  ".time-slot-calendar__lane-btn",
  ".time-slot-calendar__slot-label:not(.selected)",
  ".time-slot-event",
  ".calendar-overflow-badge",
  ".calendar-overflow-list__item",
  ".cell-dropdown-item",
  ".axis-tag-group-th--tagged",
  ".axis-tag-chip",
  ".superfilter-toggle",
  ".gutter",
].join(", ");

const getAxisPointTags = (dim: Dimension, point: Point) =>
  dim.tags.filter((tag) => (point.tagRefs ?? []).includes(tag.id));

const getPrimaryAxisTag = (dim: Dimension, point: Point) =>
  getAxisPointTags(dim, point)[0] ?? null;

const hasSplitPrimaryAxisTagGroups = (dim: Dimension, points: Point[]) => {
  if (dim.tags.length === 0) return false;

  const closedTagIds = new Set<string | null>();
  let currentTagId: string | null | undefined;

  for (const point of points) {
    const tagId = getPrimaryAxisTag(dim, point)?.id ?? null;
    if (tagId === currentTagId) continue;

    if (closedTagIds.has(tagId)) return true;
    if (currentTagId !== undefined) closedTagIds.add(currentTagId);
    currentTagId = tagId;
  }

  return false;
};

const groupAxisPointsByPrimaryTag = (dim: Dimension, points: Point[]) => {
  if (!hasSplitPrimaryAxisTagGroups(dim, points)) return points;

  const tagOrder = new Map(dim.tags.map((tag, index) => [tag.id, index]));
  const pointOrder = new Map(points.map((point, index) => [point.id, index]));
  const untaggedRank = dim.tags.length;

  return [...points].sort((a, b) => {
    const aTagId = getPrimaryAxisTag(dim, a)?.id;
    const bTagId = getPrimaryAxisTag(dim, b)?.id;
    const aRank = aTagId ? tagOrder.get(aTagId) ?? untaggedRank : untaggedRank;
    const bRank = bTagId ? tagOrder.get(bTagId) ?? untaggedRank : untaggedRank;

    if (aRank !== bRank) return aRank - bRank;
    return (pointOrder.get(a.id) ?? 0) - (pointOrder.get(b.id) ?? 0);
  });
};

const buildAxisTagLanes = (
  dim: Dimension | undefined,
  points: Point[],
  activeTagId: string | null
): AxisTagLane[] => {
  if (!dim || dim.tags.length === 0) return [];

  const tags = activeTagId
    ? dim.tags.filter((tag) => tag.id === activeTagId)
    : dim.tags;

  return tags.flatMap((tag) => {
    const spans: AxisTagSpan[] = [];
    let start: number | null = null;

    points.forEach((point, index) => {
      const hasTag = point.tagRefs.includes(tag.id);
      if (hasTag && start === null) start = index;
      if ((!hasTag || index === points.length - 1) && start !== null) {
        const end = hasTag && index === points.length - 1 ? index + 1 : index;
        spans.push({ start, count: end - start });
        start = null;
      }
    });

    if (spans.length === 0) {
      return [];
    }

    return [{ tagId: tag.id, tagName: tag.name, spans }];
  });
};

/**
 * Props-Typ für die Plan-Komponente.
 * - data: das komplette Datenmodell (Dimensionen, Plan, Settings, …)
 * - mode: aktueller Darstellungsmodus / Theme-Modus (aus colorUtils)
 * - user: aktuell eingeloggter Benutzer
 * - onLogout: Callback für Logout-Button
 */
type Props = {
  data: DataModel | null;
  mode: Mode;
  user: UserInfo | null;
  onLogout: () => void;
  onToggleAdmin?: () => void;
  adminCollapsed?: boolean;
  // Forum nach Login (aktuell deaktiviert, jetzt nur Link-Button)
  // onToggleForum?: () => void;
  // forumCollapsed?: boolean;
  projectName: string;
  projectId?: number;
  schedulerStatus?: string;
  dbDown?: boolean;
  onProjectChange?: (newProj: ProjectDetail | null) => void;
};

/**
 * Hauptkomponente zur Anzeige des Plans:
 * - Dimensionen in der Sidebar (inkl. Drag & Drop zur Sortierung)
 * - Tabelle mit 2 Achsen-Dimensionen (Spalte/Zeile)
 * - Kontextabhängige Selektion und Hervorhebung
 */
// hier gibt es was zu forum , wird aber auskomentiert
export default function Plan({ data, mode, user, onLogout, onToggleAdmin, adminCollapsed, /* onToggleForum, forumCollapsed, */ projectName, projectId, schedulerStatus, dbDown, onProjectChange }: Props) {
  // Fallback: Wenn noch keine Daten geladen wurden, einfache Meldung anzeigen
  if (!data) {
    return <div style={{ padding: "16px" }}>no plan loaded</div>;
  }

  // Wenn SF aktiv ist, markieren wir ausgewählte Items direkt in der Liste.
  const SuperFilterStyles = () => (
    <style>{`
      .superfilter-selected {
        position: relative;
        display: inline-block;
        font-weight: 600;
        cursor: grab;
        padding: 1px 5px;
        border-radius: 4px;
        border: 2px solid var(--superfilter-color, #888);
        box-sizing: border-box;
      }
      .superfilter-selected:active {
        cursor: grabbing;
      }
    `}</style>
  );

  /**
   * rows: die eingelesenen CSV-Zeilen als Array von Records
   * headers: Spaltenüberschriften der CSV
   */
  const [rows, setRows] = useState<PlanRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);

  /**
   * dimensionOrder: speichert die Reihenfolge der Dimensionen (für Drag & Drop).
   * draggedDimId: ID der Dimension, die aktuell gezogen wird (Drag & Drop).
   */
  const [dimensionOrder, setDimensionOrder] = useState<string[]>([]);
  const [draggedDimId, setDraggedDimId] = useState<string | null>(null);

  /** ——— Tag-Filter für X- und Y-Achse (Normal- und Kalender-Ansicht) ——— */
  const [wdTagFilter, setWdTagFilter] = useState<string | null>(null);
  const [phTagFilter, setPhTagFilter] = useState<string | null>(null);
  const [hoveredWdTagId, setHoveredWdTagId] = useState<string | null>(null);

  /** ——— Selektionstyp (ein Element zur Zeit) ——— */
  /**
   * Union-Typ für alle möglichen Selektionsarten:
   * - dimension: ganze Dimension ausgewählt
   * - tag: ein Tag innerhalb einer Dimension
   * - point: ein Punkt innerhalb einer Dimension
   * - cell: eine Plan-Zelle (Kombination aus Spalten-/Zeilenpunkt)
   * - weekdayPoint: Punkt auf der Spalten-Achse (z. B. Wochentag)
   * - phasePoint: Punkt auf der Zeilen-Achse (z. B. Phase/Stunde)
   */
  type Selected =
    | { type: "dimension"; dimId: string }
    | { type: "tag"; dimId: string; tagId: string }
    | { type: "point"; dimId: string; pointId: string }
    | {
      type: "cell";
      weekdayDimId: string;
      phaseDimId: string;
      weekday: string;
      phase: string;
    }
    | {
      type: "calendarEvent";
      eventId: string;
      rowIndex: string;
      weekdayDimId: string;
      phaseDimId: string;
      weekday: string;
      phase: string;
    }
    | { type: "weekdayPoint"; dimId: string; id: string; name: string }
    | { type: "phasePoint"; dimId: string; id: string; name: string };

  // aktuell ausgewähltes Element (oder null, wenn nichts gewählt)
  const [selected, setSelected] = useState<Selected | null>(null);

  // Browser feuert nach HTML5-DnD teils noch ein Click auf dem Source-Element.
  // Das führt zu "hängender" Auswahlmarkierung nach Drop in Conditions.
  // Wir unterdrücken Selection-Klicks kurz nach Drag Start/End.
  const suppressSelectionClickUntilTsRef = React.useRef(0);
  const suppressSelectionClickShortly = () => {
    suppressSelectionClickUntilTsRef.current = Date.now() + 250;
  };
  const shouldIgnoreSelectionClick = () => Date.now() < suppressSelectionClickUntilTsRef.current;

  /** ——— Superfilter (neue Perspektive vor der normalen Selektion) ———
   * pro Dimension:
   * - enabled: Checkbox neben Dimension
   * - items: ausgewählte Points/Tags dieser Dimension
   */
  const [superFilterEnabledByDimId, setSuperFilterEnabledByDimId] = useState<Record<string, boolean>>({});
  const [superFilterItemsByDimId, setSuperFilterItemsByDimId] = useState<Record<string, SuperFilterItem[]>>({});

  const toggleSuperFilterForDim = (dimId: string) => {
    setSuperFilterEnabledByDimId((prev) => {
      const nextEnabled = !prev[dimId];
      if (nextEnabled) {
        // Automatically select the first item (tag or point) when enabling
        const dim = data?.dimensions.find(d => d.id === dimId);
        if (dim) {
          const firstItem: SuperFilterItem | undefined = dim.tags.length > 0
            ? { kind: "tag", id: dim.tags[0].id }
            : dim.points.length > 0
              ? { kind: "point", id: dim.points[0].id }
              : undefined;

          if (firstItem) {
            setSuperFilterItemsByDimId((prevItems) => ({ ...prevItems, [dimId]: [firstItem] }));
          }
        }

        // Clear selection if it belongs to this dimension
        if (
          selected &&
          (("dimId" in selected && selected.dimId === dimId) ||
            ("weekdayDimId" in selected && selected.weekdayDimId === dimId) ||
            ("phaseDimId" in selected && selected.phaseDimId === dimId))
        ) {
          setSelected(null);
        }

      } else {
        // Clear selection when disabling
        setSuperFilterItemsByDimId((prevItems) => ({ ...prevItems, [dimId]: [] }));
      }
      return { ...prev, [dimId]: nextEnabled };
    });
  };

  const setSuperFilterSelectionForDim = (dimId: string, items: SuperFilterItem[]) => {
    setSuperFilterItemsByDimId((prev) => ({ ...prev, [dimId]: items }));
  };

  const removeSuperFilterItem = (dimId: string, item: SuperFilterItem) => {
    setSuperFilterItemsByDimId((prev) => {
      const cur = prev[dimId] ?? [];
      const next = cur.filter((x) => !(x.kind === item.kind && x.id === item.id));
      return { ...prev, [dimId]: next };
    });
  };

  const toggleSuperFilterItem = (dimId: string, item: SuperFilterItem) => {
    // Single-Select pro Dimension:
    // - Klick auf anderes Item -> ersetzt Auswahl
    // - Klick auf gleiches Item -> entfernt Auswahl
    setSuperFilterItemsByDimId((prev) => {
      const cur = prev[dimId] ?? [];
      const exists = cur.some((x) => x.kind === item.kind && x.id === item.id);
      const next = exists ? [] : [item];
      return { ...prev, [dimId]: next };
    });
  };

  // Wheel/Trackpad: Trackpad feuert sehr viele Events – wir drosseln pro Dimension etwas.
  const lastWheelTsByDimId = React.useRef<Record<string, number>>({});

  const sfDragActive = React.useRef(false);
  const sfDragDimId = React.useRef<string | null>(null);
  const sfPointerId = React.useRef<number | null>(null);

  const handleSfPointerDown = (e: React.PointerEvent, dimId: string) => {
    // Nur wenn SF aktiv ist und ein Item ausgewählt ist
    if (!superFilterEnabledByDimId[dimId]) return;
    const cur = superFilterItemsByDimId[dimId] ?? [];
    if (cur.length === 0) return;

    sfDragActive.current = true;
    sfDragDimId.current = dimId;
    sfPointerId.current = e.pointerId;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
    beginDragHoverSuppression();
  };

  const handleSfPointerMove = (e: React.PointerEvent) => {
    if (!sfDragActive.current || !sfDragDimId.current) return;

    // Pointer-Capture temporär aufheben, damit elementFromPoint funktioniert
    const captureTarget = e.target as HTMLElement;
    try { captureTarget.releasePointerCapture(e.pointerId); } catch (e) { }

    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;

    // Pointer-Capture wieder herstellen
    try { captureTarget.setPointerCapture(e.pointerId); } catch (e) { }

    if (!el) return;
    const sfEl = el.closest("[data-sf-dim]") as HTMLElement | null;
    if (!sfEl) return;
    const elDim = sfEl.getAttribute("data-sf-dim");
    const elKind = sfEl.getAttribute("data-sf-kind") as SuperFilterKind | null;
    const elId = sfEl.getAttribute("data-sf-id");
    if (!elDim || !elKind || !elId) return;
    if (elDim !== sfDragDimId.current) return;

    // Nur wechseln, wenn es ein anderes Item ist
    setSuperFilterItemsByDimId((prev) => {
      const cur = prev[elDim] ?? [];
      const already = cur.length === 1 && cur[0].kind === elKind && cur[0].id === elId;
      if (already) return prev;
      return { ...prev, [elDim]: [{ kind: elKind, id: elId }] };
    });
  };

  const handleSfPointerUp = (e: React.PointerEvent) => {
    sfDragActive.current = false;
    sfDragDimId.current = null;
    if (sfPointerId.current !== null) {
      try {
        (e.target as HTMLElement).releasePointerCapture(sfPointerId.current);
      } catch (err) {
        // Ignore if element is unmounted or capture already lost
      }
      sfPointerId.current = null;
    }
    endPointerDragHoverSuppression();
  };

  /**
   * Pager-Index pro Plan-Zelle (weekday|phase) für Multi-Entries.
   * Damit kann man innerhalb einer Zelle durch mehrere Einträge blättern.
   */
  const [cellEntryIndexByKey, setCellEntryIndexByKey] = useState<Record<string, number>>({});

  /**
   * Merkt sich, ob eine Multi-Entry-Zelle schon "geöffnet" wurde.
   * Solange sie nicht geöffnet wurde, zeigt die Zelle (wie früher) nur die Anzahl.
   */
  const [cellPagerOpenByKey, setCellPagerOpenByKey] = useState<Record<string, boolean>>({});

  /** 
   * Merkt sich, ob für eine Zelle die Dropdown-Liste (Long-Press) offen ist.
   */
  const [cellListOpenByKey, setCellListOpenByKey] = useState<Record<string, boolean>>({});

  /** 
   * Merkt sich, wie die Liste geöffnet wurde: 'hover' oder 'longpress'
   * Hover → schließt bei Mouse-Leave
   * Long-Press → bleibt offen bis Klick woanders
   */
  const [cellListOpenMethod, setCellListOpenMethod] = useState<Record<string, 'hover' | 'longpress'>>({});
  const [openCalendarOverflowGroupId, setOpenCalendarOverflowGroupId] = useState<string | null>(null);
  const [calendarOverflowPortalRect, setCalendarOverflowPortalRect] = useState<{ top: number; left: number; width: number } | null>(null);

  /** Portal-Ansatz für Normal-Ansicht Dropdown (wie Kalender-Overflow) */
  const [openNormalViewDropdownKey, setOpenNormalViewDropdownKey] = useState<string | null>(null);
  const [normalViewPortalRect, setNormalViewPortalRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [normalViewPortalRows, setNormalViewPortalRows] = useState<PlanRow[]>([]);

  /**
   * Timer je Zelle, damit parallele Hovers/Long-Presses sich nicht gegenseitig überschreiben.
   */
  const longPressTimerByKey = React.useRef<Record<string, number | null>>({});
  const hoverTimerByKey = React.useRef<Record<string, number | null>>({});
  const planHoveredElement = React.useRef<HTMLElement | null>(null);

  /** Ref, um zu merken, ob ein Long-Press ausgelöst wurde, damit der nachfolgende Click ignoriert wird */
  const isLongPressTriggered = React.useRef(false);

  const clearHoverTimers = React.useCallback(() => {
    Object.values(hoverTimerByKey.current).forEach((timer) => {
      if (timer) window.clearTimeout(timer);
    });
    hoverTimerByKey.current = {};
  }, []);

  const clearManagedHover = React.useCallback(() => {
    planHoveredElement.current?.classList.remove("is-plan-hovered");
    planHoveredElement.current = null;
  }, []);

  const resetTransientHoverState = React.useCallback(() => {
    clearHoverTimers();
    clearManagedHover();
    setHoveredWdTagId(null);
    setCellListOpenByKey({});
    setCellListOpenMethod({});
    setOpenCalendarOverflowGroupId(null);
    setCalendarOverflowPortalRect(null);
    setOpenNormalViewDropdownKey(null);
    setNormalViewPortalRect(null);
    setNormalViewPortalRows([]);
  }, [clearHoverTimers, clearManagedHover]);

  const beginDragHoverSuppression = React.useCallback(() => {
    resetTransientHoverState();
    document.body.classList.add("is-dragging");
  }, [resetTransientHoverState]);

  const endPointerDragHoverSuppression = React.useCallback(() => {
    resetTransientHoverState();
    document.body.classList.remove("is-dragging");
  }, [resetTransientHoverState]);

  useEffect(() => {
    const findManagedHoverTarget = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return null;
      return target.closest(PLAN_HOVER_SELECTOR) as HTMLElement | null;
    };

    const setManagedHover = (next: HTMLElement | null) => {
      if (planHoveredElement.current === next) return;
      clearManagedHover();
      if (!next) return;
      next.classList.add("is-plan-hovered");
      planHoveredElement.current = next;
    };

    const isHoverSuppressed = () =>
      document.body.classList.contains("is-dragging");

    const handleMouseOver = (event: MouseEvent) => {
      if (isHoverSuppressed()) {
        clearManagedHover();
        return;
      }

      const next = findManagedHoverTarget(event.target);
      if (!next) return;
      const previous = findManagedHoverTarget(event.relatedTarget);
      if (previous === next) return;
      setManagedHover(next);
    };

    const handleMouseOut = (event: MouseEvent) => {
      const current = planHoveredElement.current;
      if (!current) return;

      const related = event.relatedTarget;
      if (related instanceof Node && current.contains(related)) return;

      const next = findManagedHoverTarget(related);
      if (next === current) return;
      if (isHoverSuppressed()) {
        clearManagedHover();
        return;
      }
      setManagedHover(next);
    };

    window.addEventListener("mouseover", handleMouseOver, true);
    window.addEventListener("mouseout", handleMouseOut, true);
    window.addEventListener("blur", clearManagedHover);

    return () => {
      window.removeEventListener("mouseover", handleMouseOver, true);
      window.removeEventListener("mouseout", handleMouseOut, true);
      window.removeEventListener("blur", clearManagedHover);
      clearManagedHover();
    };
  }, [clearManagedHover]);

  /**
   * Native HTML5 drags can swallow mouseleave. Keep managed hover suppressed
   * until the next real mousemove/mousedown, then clear transient React state.
   */
  useEffect(() => {
    let clearFrame: number | null = null;

    function removeArmedClear() {
      if (clearFrame !== null) {
        window.cancelAnimationFrame(clearFrame);
        clearFrame = null;
      }
      window.removeEventListener("mousemove", clearDragHover, true);
      window.removeEventListener("mousedown", clearDragHover, true);
    }

    function clearDragHover() {
      removeArmedClear();
      resetTransientHoverState();
      document.body.classList.remove("is-dragging");
    }

    function armClearAfterBrowserSettles() {
      resetTransientHoverState();
      removeArmedClear();
      clearFrame = window.requestAnimationFrame(() => {
        clearFrame = null;
        window.addEventListener("mousemove", clearDragHover, true);
        window.addEventListener("mousedown", clearDragHover, true);
      });
    }

    function onDragStart() {
      removeArmedClear();
      beginDragHoverSuppression();
    }

    window.addEventListener("dragstart", onDragStart, true);
    window.addEventListener("dragend", armClearAfterBrowserSettles, true);
    window.addEventListener("drop", armClearAfterBrowserSettles, true);
    window.addEventListener("dragcancel", armClearAfterBrowserSettles, true);

    return () => {
      window.removeEventListener("dragstart", onDragStart, true);
      window.removeEventListener("dragend", armClearAfterBrowserSettles, true);
      window.removeEventListener("drop", armClearAfterBrowserSettles, true);
      window.removeEventListener("dragcancel", armClearAfterBrowserSettles, true);
      removeArmedClear();
      document.body.classList.remove("is-dragging");
    };
  }, [beginDragHoverSuppression, resetTransientHoverState]);

  /** ——— Nachschlagewerke über Admin-Daten ——— */

  /**
   * dimById:
   * Map von Dimension-ID -> Dimension-Objekt
   * Erleichtert das schnelle Nachschlagen von Dimensionen anhand der ID.
   */
  const dimById = useMemo(() => {
    const m = new Map<string, (typeof data.dimensions)[number]>();
    data.dimensions.forEach((d) => m.set(d.id, d));
    return m;
  }, [data.dimensions]);

  /**
   * pointIdToName:
   * Map von Punkt-ID -> { name, dimId }
   * Damit kann man aus einem Punkt-ID direkt den Namen und die zugehörige Dimension finden.
   */
  const pointIdToName = useMemo(() => {
    const m = new Map<string, { name: string; dimId: string }>();
    data.dimensions.forEach((d) =>
      d.points.forEach((p) => m.set(p.id, { name: p.name, dimId: d.id }))
    );
    return m;
  }, [data.dimensions]);

  /**
   * tagIdToPointNames:
   * Map von Tag-ID -> Set von Punktnamen, die dieses Tag referenzieren.
   * Wird für Tag-Filter und Assoziationsanzeigen verwendet.
   */
  const tagIdToPointNames = useMemo(() => {
    const m = new Map<string, Set<string>>();
    data.dimensions.forEach((d) => {
      d.tags.forEach((t) => {
        const names = new Set(
          d.points
            .filter((p) => p.tagRefs.includes(t.id))
            .map((p) => p.name)
        );
        m.set(t.id, names);
      });
    });
    return m;
  }, [data.dimensions]);

  /** ——— DimensionOrder initialisieren / pflegen (aus 0.4) ——— */
  /**
   * DimensionOrder nur neu setzen, wenn sich die IDs wirklich geändert haben
   * (neue/gelöschte Dimensionen). Reine Feld-Updates (z. B. calendarMaxLanes)
   * ändern die IDs nicht → kein Reset der Achsen-Reihenfolge.
   */
  const prevDimIdsRef = React.useRef<string>("");
  useEffect(() => {
    const ids = data.dimensions.map((d) => d.id);
    const idsKey = ids.join(",");
    if (idsKey === prevDimIdsRef.current) return; // Nur IDs-Änderung triggert Reset
    prevDimIdsRef.current = idsKey;
    setDimensionOrder(ids);
  }, [data.dimensions]);

  /**
   * orderedDims:
   * - bildet die Dimensionen gemäß dimensionOrder ab
   * - ergänzt ggf. Dimensionen, die noch nicht in dimensionOrder enthalten sind (missing)
   *   -> so bleiben neue Dimensionen nicht "unsichtbar".
   */
  const orderedDims = useMemo(() => {
    const idToDim = new Map(data.dimensions.map((d) => [d.id, d]));
    const mapped = dimensionOrder
      .map((id) => idToDim.get(id))
      // Filter stellt sicher, dass nur existierende Dimensionen verwendet werden
      .filter((dim): dim is (typeof data.dimensions)[number] => !!dim);
    const missing = data.dimensions.filter((d) => !dimensionOrder.includes(d.id));
    return [...mapped, ...missing];
  }, [dimensionOrder, data.dimensions]);

  /** ——— Zeit-/Achsendimensionen (0.5-Logik, aber auf orderedDims) ——— */
  /**
   * Aus den sortierten Dimensionen wird abgeleitet:
   * - weekdayDim: Dimension für die Spalten-Achse (Index 0)
   * - phaseDim: Dimension für die Zeilen-Achse (Index 1)
   * - wdPoints: Punkte der Spalten-Dimension (z. B. Wochentage)
   * - phPoints: Punkte der Zeilen-Dimension (z. B. Phasen / Stunden)
   * - otherDims: alle weiteren Dimensionen ab Index 2
   * - sidebarDims: Dimensionen, die in der Sidebar angezeigt werden
   *   (bis 2 insgesamt: alle, ab 3 nur "otherDims").
   */
  const {
    weekdayDim,
    phaseDim,
    wdPoints,
    phPoints,
    wdName,
    phName,
    otherDims,
    sidebarDims,
  } = useMemo(() => {
    const weekdayDim = orderedDims[0]; // Spalten-Achse
    const phaseDim = orderedDims[1];   // Zeilen-Achse
    const wdPoints = weekdayDim?.points ?? [];
    const phPoints = phaseDim?.points ?? [];
    const wdName = weekdayDim?.name ?? "";
    const phName = phaseDim?.name ?? "";
    const otherDims = orderedDims.slice(2);

    // Sidebar-Dimensionen:
    // - Die ersten beiden Dimensionen (Achsen) werden nur im Plan dargestellt.
    // - Erst ab der 3. Dimension in der Sidebar anzeigen (andere Dimensionen).
    const sidebarDims = otherDims;

    return {
      weekdayDim,
      phaseDim,
      wdPoints,
      phPoints,
      wdName,
      phName,
      otherDims,
      sidebarDims,
    };
  }, [orderedDims]);

  /** ——— Tag-Filter für Achsen-Punkte (Normal- und Kalender-Ansicht) ——— */
  /** Reset wenn sich die Achsen-Dimensionen ändern */
  const prevAxisIdsRef = React.useRef("");
  useEffect(() => {
    const key = `${weekdayDim?.id ?? ""}|${phaseDim?.id ?? ""}`;
    if (key !== prevAxisIdsRef.current) {
      prevAxisIdsRef.current = key;
      setWdTagFilter(null);
      setPhTagFilter(null);
    }
  }, [weekdayDim?.id, phaseDim?.id]);

  /** Nur die Spalten-Punkte anzeigen, die zum gewählten Tag gehören (oder alle wenn kein Filter) */
  const displayWdPoints = useMemo(() => {
    const visiblePoints = wdTagFilter
      ? wdPoints.filter((p) => p.tagRefs.includes(wdTagFilter))
      : wdPoints;

    return weekdayDim && !wdTagFilter
      ? groupAxisPointsByPrimaryTag(weekdayDim, visiblePoints)
      : visiblePoints;
  }, [weekdayDim, wdPoints, wdTagFilter]);
  /** Nur die Zeilen-Punkte anzeigen, die zum gewählten Tag gehören (oder alle wenn kein Filter) */
  const displayPhPoints = useMemo(() => {
    const visiblePoints = phTagFilter
      ? phPoints.filter((p) => p.tagRefs.includes(phTagFilter))
      : phPoints;

    return phaseDim
      ? groupAxisPointsByPrimaryTag(phaseDim, visiblePoints)
      : visiblePoints;
  }, [phaseDim, phPoints, phTagFilter]);

  /**
   * Tag-Lanes zeigen jede Tag-Zugehörigkeit separat.
   * Überlappungen bleiben sichtbar, ohne Punkte zu neuen Kombi-Gruppen zu machen.
   * Beispiel: Mo–Fr → "Werktag", Fr–So → "Wochenende"; Freitag liegt unter beiden Linien.
   */
  const wdTagLanes = useMemo<AxisTagLane[]>(() => {
    return buildAxisTagLanes(weekdayDim, displayWdPoints, null);
  }, [weekdayDim, displayWdPoints]);

  const wdAxisTags = weekdayDim?.tags ?? [];
  const hasWdTagGroups = wdAxisTags.length > 0;
  const focusedWdTagId = hoveredWdTagId ?? wdTagFilter;
  const focusedWdTagLane = focusedWdTagId
    ? wdTagLanes.find((lane) => lane.tagId === focusedWdTagId)
    : undefined;

  const phAxisTags = phaseDim?.tags ?? [];
  const hasPhTagGroups = phAxisTags.length > 0;

  const renderWeekdayTagChip = (tagId: string, tagName: string) => {
    if (!weekdayDim) return null;
    const isActive = wdTagFilter === tagId;

    return (
      <div
        className={`axis-tag-chip${isActive ? " axis-tag-chip--active" : ""}`}
        title={tagName}
        draggable
        onMouseEnter={() => {
          if (document.body.classList.contains("is-dragging")) return;
          setHoveredWdTagId(tagId);
        }}
        onMouseLeave={() => setHoveredWdTagId((current) => current === tagId ? null : current)}
        onDragStart={(e) => {
          suppressSelectionClickShortly();
          e.dataTransfer.effectAllowed = "copy";
          e.dataTransfer.setData(
            "application/x-zeitgeist",
            JSON.stringify({
              kind: "create-condition-from-dimension-tag",
              dimensionId: weekdayDim.id,
              dimensionName: wdName,
              tagId,
              tagName,
            })
          );
        }}
        onClick={(e) => {
          e.stopPropagation();
          setWdTagFilter(isActive ? null : tagId);
          if (projectId) notifyProjectActivity(projectId).catch(() => { });
        }}
      >
        {tagName}
      </div>
    );
  };

  const renderPhaseTagChip = (tagId: string, tagName: string) => {
    if (!phaseDim) return null;
    const isActive = phTagFilter === tagId;

    return (
      <div
        className={`axis-tag-chip${isActive ? " axis-tag-chip--active" : ""}`}
        title={tagName}
        draggable
        onDragStart={(e) => {
          suppressSelectionClickShortly();
          e.dataTransfer.effectAllowed = "copy";
          e.dataTransfer.setData(
            "application/x-zeitgeist",
            JSON.stringify({
              kind: "create-condition-from-dimension-tag",
              dimensionId: phaseDim.id,
              dimensionName: phName,
              tagId,
              tagName,
            })
          );
        }}
        onClick={(e) => {
          e.stopPropagation();
          setPhTagFilter(isActive ? null : tagId);
          if (projectId) notifyProjectActivity(projectId).catch(() => { });
        }}
      >
        {tagName}
      </div>
    );
  };

  const renderWeekdayTagLens = (columnCount: number) => (
    <div className="axis-tag-lens" onMouseLeave={() => setHoveredWdTagId(null)}>
      <div className="axis-tag-lens__chips">
        {wdAxisTags
          .filter((tag) => !wdTagFilter || tag.id === wdTagFilter)
          .map((tag) => (
            <React.Fragment key={tag.id}>
              {renderWeekdayTagChip(tag.id, tag.name)}
            </React.Fragment>
          ))}
      </div>
      {focusedWdTagLane && (
        <div
          className="axis-tag-lens__track"
          style={{ gridTemplateColumns: `repeat(${Math.max(1, columnCount)}, minmax(0, 1fr))` }}
        >
          {focusedWdTagLane.spans.map((span, spanIndex) => (
            <div
              key={`${focusedWdTagLane.tagId}-span-${spanIndex}`}
              className="axis-tag-lens__segment"
              style={{ gridColumn: `${span.start + 1} / span ${span.count}` }}
            />
          ))}
        </div>
      )}
    </div>
  );

  /**
   * superFilteredRows:
   * Erst Superfilter (neue Perspektive) anwenden.
   * - pro aktivierter Dimension werden nur die Zeilen behalten,
   *   die zu einem ausgewählten Point oder Tag passen.
   * - Wenn keine Items in einer aktivierten Dimension gewählt sind,
   *   lassen wir diese Dimension "neutral" (kein Filter).
   */
  const superFilteredRows = useMemo(() => {
    let cur = rows;

    for (const dim of data.dimensions) {
      if (!superFilterEnabledByDimId[dim.id]) continue;

      const items = superFilterItemsByDimId[dim.id] ?? [];
      if (items.length === 0) continue;

      const pointNameAllow = new Set<string>();

      for (const it of items) {
        if (it.kind === "point") {
          const p = dim.points.find((x) => x.id === it.id);
          if (p) pointNameAllow.add(p.name);
        }
        if (it.kind === "tag") {
          const pointsForTag = dim.points
            .filter((p) => p.tagRefs.includes(it.id))
            .map((p) => p.name);
          pointsForTag.forEach((n) => pointNameAllow.add(n));
        }
      }

      // Falls die Auswahl ungültig ist (keine Namen), lassen wir safe: alles wegfiltern.
      // (Das ist besser als "alles" zeigen, denn der User hat ja was ausgewählt.)
      cur = cur.filter((r) => pointNameAllow.has(r[dim.name]));
    }

    return cur;
  }, [rows, data.dimensions, superFilterEnabledByDimId, superFilterItemsByDimId]);

  /**
   * filteredRows:
   * Alle Zeilen, die im aktuellen "sichtbaren" Datensatz erscheinen.
   * - Zuerst Superfilter, dann normale Selektion (Point/Tag).
   * - Bei anderen Selektionsarten bleibt der Datensatz unverändert.
   */
  const filteredRows = useMemo(() => {
    const base = superFilteredRows;
    if (!selected) return base;

    // Filtern nach ausgewähltem Punkt
    if (selected.type === "point") {
      const dim = dimById.get(selected.dimId);
      if (!dim) return base;
      const p = pointIdToName.get(selected.pointId);
      return p ? base.filter((r) => r[dim.name] === p.name) : base;
    }

    // Filtern nach ausgewähltem Tag
    if (selected.type === "tag") {
      const dim = dimById.get(selected.dimId);
      if (!dim) return base;
      const set = tagIdToPointNames.get(selected.tagId);
      return set ? base.filter((r) => set.has(r[dim.name])) : base;
    }

    // Für alle anderen Selektionsarten keine zusätzliche Filterung
    return base;
  }, [superFilteredRows, selected, dimById, pointIdToName, tagIdToPointNames]);

  // Wenn sich der Datensatz durch Plan-Wechsel oder Filter stark ändert, Pager-Indices zurücksetzen.
  // (Sonst könnten Indices auf nicht mehr existierende Einträge zeigen.)
  useEffect(() => {
    setCellEntryIndexByKey({});
    setCellPagerOpenByKey({});
    setCellListOpenByKey({});
    setCellListOpenMethod({});
    setOpenCalendarOverflowGroupId(null);
    setOpenNormalViewDropdownKey(null);
    setNormalViewPortalRect(null);
    setNormalViewPortalRows([]);

  }, [data.plan_csv, selected]);

  /** 
   * Globaler Click-Listener, um Dropdowns zu schließen, wenn man außerhalb klickt.
   */
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      // Wenn ein Long-Press gerade beendet wurde, ignoriere alle globalen Ghost-Clicks
      if (isLongPressTriggered.current) return;

      const target = e.target as HTMLElement;
      // Wenn der Klick nicht innerhalb einer Dropdown-Liste war, alles schließen
      if (!target.closest(".cell-dropdown-list") && !target.closest(".calendar-overflow-list") && !target.closest(".calendar-overflow-badge")) {
        // Wir setzen alle auf false. (Leeres Objekt reicht nicht, falls wir keys explizit auf false setzen wollen, 
        // aber {} ist hier state standard: 'nicht vorhanden = false').
        // Um sicherzugehen, prüfen wir, ob überhaupt was offen ist, um Renders zu sparen?
        // Einfachheitshalber: immer resetten oder checken.
        setCellListOpenByKey((prev) => {
          // Nur update feuern, wenn es keys gibt, die true sind
          const hasOpen = Object.values(prev).some((v) => v);
          return hasOpen ? {} : prev;
        });
        setCellListOpenMethod({}); // Auch Öffnungsmodus zurücksetzen
        setOpenCalendarOverflowGroupId(null);
        setOpenNormalViewDropdownKey(null);
        setNormalViewPortalRect(null);
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => {
      document.removeEventListener("click", handleGlobalClick);
    };
  }, []);

  // Timer beim Unmount aufräumen
  useEffect(() => {
    return () => {
      Object.values(longPressTimerByKey.current).forEach((t) => {
        if (t) window.clearTimeout(t);
      });
      Object.values(hoverTimerByKey.current).forEach((t) => {
        if (t) window.clearTimeout(t);
      });
    };
  }, []);

  /**
   * visibleCells:
   * Set aller Zellen (kombiniert aus weekday|phase), die im gefilterten Datensatz vorkommen.
   * Dient zur Entscheidung, ob eine Zelle als "belegt" / sichtbar markiert wird.
   */
  const visibleCells = useMemo(() => {
    const s = new Set<string>();
    if (!weekdayDim) return s;
    filteredRows.forEach((r) => {
      const wdVal = r[weekdayDim.name];
      const phVal = phaseDim ? r[phaseDim.name] : "";
      if (!wdVal || (phaseDim && !phVal)) return;
      s.add(`${wdVal}|${phVal}`);
    });
    return s;
  }, [filteredRows, weekdayDim, phaseDim]);

  /**
   * rowsForAssociations:
   * Untermenge von rows/filteredRows, die für die Assoziationsanzeige benutzt wird.
   * Je nach Selektion werden andere Reihen herangezogen:
   * - point/tag: gefilterte Zeilen
   * - weekdayPoint/phasePoint: alle Zeilen, in denen dieser Punkt vorkommt
   * - cell: alle Zeilen, die genau zu dieser Zelle (weekday+phase) gehören
   */
  const rowsForAssociations = useMemo(() => {
    if (!selected) return [] as PlanRow[];

    if (selected.type === "point" || selected.type === "tag") {
      // Bei Punkt/Tag-Selektion nehmen wir direkt die gefilterten Zeilen.
      return filteredRows;
    }

    if (selected.type === "weekdayPoint" || selected.type === "phasePoint") {
      const dim = dimById.get(selected.dimId);
      if (!dim) return [];
      return rows.filter((r) => r[dim.name] === selected.name);
    }

    if (selected.type === "cell") {
      const wdDim = dimById.get(selected.weekdayDimId);
      const phDim = selected.phaseDimId ? dimById.get(selected.phaseDimId) : undefined;
      if (!wdDim) return [];
      return rows.filter(
        (r) =>
          r[wdDim.name] === selected.weekday && (!phDim || r[phDim.name] === selected.phase)
      );
    }

    if (selected.type === "calendarEvent") {
      return filteredRows.filter((r) => r.__rowIndex === selected.rowIndex);
    }

    return [];
  }, [selected, rows, filteredRows, dimById]);

  /**
   * associatedNamesByDim:
   * Map<dimId, Set(pointName)>
   * Für jede Dimension wird berechnet, welche Punktnamen in den rowsForAssociations vorkommen.
   * Achsen-Dimensionen werden explizit übersprungen, damit sie nicht in der Sidebar-Assoziation auftauchen.
   */
  const associatedNamesByDim = useMemo(() => {
    const m = new Map<string, Set<string>>();
    if (rowsForAssociations.length === 0) return m;

    // Initial: für jede Dimension ein leeres Set
    data.dimensions.forEach((d) => m.set(d.id, new Set<string>()));

    // Durch alle assoziierten Reihen laufen und Punktnamen den Dimensionen zuordnen.
    rowsForAssociations.forEach((r) => {
      data.dimensions.forEach((d) => {
        // Achsen-Dimensionen auslassen
        if (d.id === weekdayDim?.id || d.id === phaseDim?.id) return;

        const nameInRow = r[d.name];
        if (!nameInRow) return;

        // Sicherstellen, dass der Name einem existierenden Punkt entspricht
        const isValidPoint = d.points.some((p) => p.name === nameInRow);
        if (isValidPoint) {
          m.get(d.id)!.add(nameInRow);
        }
      });
    });

    return m;
  }, [rowsForAssociations, data.dimensions, weekdayDim?.id, phaseDim?.id]);

  /** ——— Theme ——— */
  /**
   * Setzt CSS-Variablen für das Theme anhand des Hue-Werts aus den Settings.
   * makeThemeVars berechnet aus dem Hue die benötigten CSS Custom Properties.
   */
  useEffect(() => {
    const hue = data.settings?.hue ?? 172;
    const vars = makeThemeVars(hue);
    Object.entries(vars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  }, [data.settings?.hue]);

  /** ——— Plan aus CSV laden ——— */
  /**
   * Liest data.plan_csv (falls vorhanden) als CSV ein:
   * - erste Zeile: Header
   * - restliche Zeilen: Daten
   * Es wird ein Array von Records aufgebaut, bei dem jede Spalte über den Header-Namen erreichbar ist.
   */
  useEffect(() => {
    if (data.plan_csv) {
      const text = data.plan_csv;
      const lines = text.trim().split("\n");
      const [headerLine, ...dataLines] = lines;
      const header = headerLine.split(";");
      setHeaders(header);

      const parsed = dataLines.map((line, rowIndex) => {
        const values = line.split(";");
        const row: PlanRow = {};
        header.forEach((h, i) => (row[h.trim()] = values[i]?.trim() || ""));
        row.__rowIndex = String(rowIndex);
        return row;
      });
      setRows(parsed);
    } else {
      // Wenn kein CSV vorhanden, State leeren
      setRows([]);
      setHeaders([]);
    }
  }, [data.plan_csv]);

  /** ——— Fulfillments aus fulfillments_json verarbeiten ——— */
  /**
   * Aktuell nur Debug-Ausgabe. Hier könnten später Statistik-Anzeigen berechnet werden.
   */
  useEffect(() => {
    if (data.fulfillments_json) {
      console.log("Fulfillments data updated:", data.fulfillments_json);
    }
  }, [data.fulfillments_json]);

  /** ——— Drag-&-Drop-Helfer (aus 0.4) ——— */

  /**
   * insertBeforeTarget:
   * Hilfsfunktion, um die gezogene Dimension (draggedDimId)
   * in dimensionOrder direkt vor einem Ziel-Element (targetId) einzufügen.
   * Gibt true zurück, wenn sich die Reihenfolge tatsächlich geändert hat.
   */
  const insertBeforeTarget = (targetId: string) => {
    if (!draggedDimId || draggedDimId === targetId) return false;
    let changed = false;
    setDimensionOrder((prev) => {
      const withoutDragged = prev.filter((id) => id !== draggedDimId);
      const targetIndex = withoutDragged.indexOf(targetId);
      if (targetIndex === -1) return prev;
      const next = [...withoutDragged];
      next.splice(targetIndex, 0, draggedDimId);

      // Prüfen, ob sich die Reihenfolge wirklich geändert hat
      const isSameOrder =
        next.length === prev.length && next.every((id, index) => id === prev[index]);
      if (isSameOrder) {
        return prev;
      }
      changed = true;
      return next;
    });
    return changed;
  };

  /**
   * handleDimDrop:
   * Wird aufgerufen, wenn die gezogene Dimension auf eine andere Dimension
   * in der Sidebar gedroppt wird – die Reihenfolge wird entsprechend angepasst.
   */
  const handleDimDrop = (targetId: string) => {
    if (!draggedDimId) return;
    if (draggedDimId === targetId) {
      setDraggedDimId(null);
      return;
    }
    const changed = insertBeforeTarget(targetId);
    if (changed) {
      setSelected(null);
      // Activity-Tracking
      if (projectId) notifyProjectActivity(projectId).catch(() => { });
    }
    setDraggedDimId(null);
  };

  /**
   * handleAxisDrop:
   * Wird aufgerufen, wenn eine Dimension auf eine Achse (Spalte/Zeile) gedroppt wird.
   * axisIndex:
   *  - 0: Spalten-Achse (weekdayDim)
   *  - 1: Zeilen-Achse (phaseDim)
   *
   * Umsetzung per einfachem Swap innerhalb von dimensionOrder.
   */
  const handleAxisDrop = (axisIndex: number) => {
    if (!draggedDimId) return;

    setDimensionOrder((prev) => {
      const draggedIndex = prev.indexOf(draggedDimId);
      if (draggedIndex === -1) return prev;

      const next = [...prev];

      // Swap: Element an axisIndex mit draggedDim vertauschen
      const temp = next[axisIndex];
      next[axisIndex] = next[draggedIndex];
      next[draggedIndex] = temp;

      return next;
    });
    setSelected(null);
    setDraggedDimId(null);
    // Activity-Tracking
    if (projectId) notifyProjectActivity(projectId).catch(() => { });
  };

  // ----- Resizing Logic (Dimensions Sidebar) -----
  const [dimsWidth, setDimsWidth] = useState(300); // Initial width
  const minDimsWidth = 150;
  const maxDimsWidth = 450; // Reduced from 800 to keep Plan visible

  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = dimsWidth;

    // Capture container width at start of drag to avoid layout thrashing
    const container = (e.currentTarget.parentElement as HTMLElement);
    const containerWidth = container ? container.getBoundingClientRect().width : window.innerWidth;

    const onMouseMove = (me: MouseEvent) => {
      let w = startWidth + (me.clientX - startX);
      if (w < minDimsWidth) w = minDimsWidth;

      // Ensure we leave enough space for the table (e.g. 100-150px min visible)
      // We calculate available width based on the PLAN CONTAINER, not window.
      const maxAllowed = Math.max(minDimsWidth, containerWidth - 100);

      // Also respect absolute max
      if (w > Math.min(maxDimsWidth, maxAllowed)) w = Math.min(maxDimsWidth, maxAllowed);

      setDimsWidth(w);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const getRowDisplayValues = (row: PlanRow) => {
    const dimsToShow = otherDims.filter((d) => {
      if (selected?.type === "point" && d.id === selected.dimId) return false;
      if (superFilterEnabledByDimId[d.id]) return false;
      return true;
    });

    return dimsToShow.map((dim) => row[dim.name]).filter(Boolean);
  };

  const slotPointByName = useMemo(() => {
    const points = new Map<string, (typeof data.dimensions)[number]["points"][number]>();
    phPoints.forEach((point) => {
      points.set(point.name, point);
    });
    return points;
  }, [data.dimensions, phPoints]);

  const visibleTimeSlotPointNames = useMemo(() => {
    return new Set(displayPhPoints.map((point) => point.name));
  }, [displayPhPoints]);

  const fallbackTimeBounds = useMemo(() => {
    let minStart = Number.POSITIVE_INFINITY;
    let maxEnd = Number.NEGATIVE_INFINITY;

    phPoints.forEach((point) => {
      const startMin = parseHHMM(point.startTime);
      const durationMinutes = point.durationMinutes ?? 0;
      if (startMin === null || durationMinutes <= 0) return;

      minStart = Math.min(minStart, startMin);
      maxEnd = Math.max(maxEnd, startMin + durationMinutes);
    });

    return {
      startMin: Number.isFinite(minStart) ? minStart : 8 * 60,
      endMin: Number.isFinite(maxEnd) ? maxEnd : 18 * 60,
    };
  }, [phPoints]);

  const timeSlotSourceEvents = useMemo(() => {
    if (!weekdayDim || !phaseDim || phaseDim.dimType !== "time.slot") return [] as {
      id: string;
      row: PlanRow;
      columnIndex: number;
      columnName: string;
      phaseName: string;
      startMin: number;
      endMin: number;
    }[];

    const columnIndexByName = new Map(displayWdPoints.map((point, index) => [point.name, index]));

    return filteredRows.reduce<{
      id: string;
      row: PlanRow;
      columnIndex: number;
      columnName: string;
      phaseName: string;
      startMin: number;
      endMin: number;
    }[]>((acc, row) => {
      const weekdayName = row[weekdayDim.name];
      const phaseName = row[phaseDim.name];
      if (!weekdayName || !phaseName) return acc;
      if (!visibleTimeSlotPointNames.has(phaseName)) return acc;

      const columnIndex = columnIndexByName.get(weekdayName);
      if (columnIndex === undefined) return acc;

      const point = slotPointByName.get(phaseName);
      const startMin = parseHHMM(point?.startTime);
      const durationMinutes = point?.durationMinutes ?? 0;
      if (startMin === null || durationMinutes <= 0) return acc;

      acc.push({
        id: row.__rowIndex ?? `${columnIndex}-${phaseName}`,
        row,
        columnIndex,
        columnName: weekdayName,
        phaseName,
        startMin,
        endMin: startMin + durationMinutes,
      });
      return acc;
    }, []);
  }, [filteredRows, phaseDim, slotPointByName, displayWdPoints, weekdayDim, visibleTimeSlotPointNames]);

  const unscheduledTimeSlotRows = useMemo(() => {
    if (!weekdayDim || !phaseDim || phaseDim.dimType !== "time.slot") return [] as PlanRow[];

    const columnIndexByName = new Map(displayWdPoints.map((point, index) => [point.name, index]));

    return filteredRows.filter((row) => {
      const weekdayName = row[weekdayDim.name];
      const phaseName = row[phaseDim.name];
      if (!weekdayName || !phaseName) return true;
      if (!visibleTimeSlotPointNames.has(phaseName)) return false;
      if (!columnIndexByName.has(weekdayName)) return true;

      const point = slotPointByName.get(phaseName);
      const startMin = parseHHMM(point?.startTime);
      const durationMinutes = point?.durationMinutes ?? 0;
      return startMin === null || durationMinutes <= 0;
    });
  }, [filteredRows, phaseDim, slotPointByName, displayWdPoints, weekdayDim, visibleTimeSlotPointNames]);

  const timeSlotLayout = useMemo(() => {
    if (!weekdayDim || !phaseDim || phaseDim.dimType !== "time.slot") return null;

    const maxLanes = phaseDim.calendarMaxLanes ?? 1;

    return buildTimeSlotLayout(timeSlotSourceEvents, {
      columnCount: displayWdPoints.length,
      fallbackStartMin: fallbackTimeBounds.startMin,
      fallbackEndMin: fallbackTimeBounds.endMin,
      maxVisibleLanes: maxLanes,
      slotStepMin: 60,
    });
  }, [fallbackTimeBounds.endMin, fallbackTimeBounds.startMin, phaseDim, timeSlotSourceEvents, displayWdPoints.length, weekdayDim]);

  const timeSlotSegmentsByColumn = useMemo(() => {
    const grouped = new Map<number, TimeSlotSegment<PlanRow>[]>();
    if (!timeSlotLayout) return grouped;

    timeSlotLayout.segments.forEach((segment) => {
      if (segment.hidden) return;
      const columnSegments = grouped.get(segment.columnIndex) ?? [];
      columnSegments.push(segment);
      grouped.set(segment.columnIndex, columnSegments);
    });

    grouped.forEach((segments) => {
      segments.sort((a, b) => {
        const byStart = a.startMin - b.startMin;
        if (byStart !== 0) return byStart;
        return a.laneIndex - b.laneIndex;
      });
    });

    return grouped;
  }, [timeSlotLayout]);

  const timeSlotOverflowGroupsByColumn = useMemo(() => {
    const grouped = new Map<number, TimeSlotOverflowGroup<PlanRow>[]>();
    if (!timeSlotLayout) return grouped;

    timeSlotLayout.overflowGroups.forEach((group) => {
      const columnGroups = grouped.get(group.columnIndex) ?? [];
      columnGroups.push(group);
      grouped.set(group.columnIndex, columnGroups);
    });

    grouped.forEach((groups) => {
      groups.sort((a, b) => a.startMin - b.startMin);
    });

    return grouped;
  }, [timeSlotLayout]);

  /**
   * timeSlotPointLabels:
   * Zeigt die Points der time.slot-Y-Dimension direkt auf der Kalender-Zeitachse.
   * Die Labels bleiben echte Point-Dragquellen für Conditions/Formeln.
   */
  const timeSlotPointLabels = useMemo(() => {
    if (!phaseDim || phaseDim.dimType !== "time.slot") {
      return [] as TimeSlotPointLabel[];
    }

    return displayPhPoints
      .map((point) => {
        const startMin = parseHHMM(point.startTime);
        if (startMin === null) return null;

        return {
          point,
          startMin,
          endMin: startMin + Math.max(0, point.durationMinutes ?? 0),
        };
      })
      .filter((label): label is TimeSlotPointLabel => label !== null)
      .sort((a, b) => a.startMin - b.startMin);
  }, [displayPhPoints, phaseDim]);

  const calendarPixelsPerMinute = 2;
  const calendarMinBlockHeight = 32;
  const calendarTimeAxisWidth = 128;
  const calendarVisibleLaneCap = phaseDim?.calendarMaxLanes ?? 1;
  const labelStartMin = timeSlotPointLabels.length > 0
    ? Math.min(...timeSlotPointLabels.map((label) => label.startMin))
    : null;
  const labelEndMin = timeSlotPointLabels.length > 0
    ? Math.max(...timeSlotPointLabels.map((label) => label.endMin))
    : null;

  // Kalenderbereich um Slot-Labels erweitern, auch wenn in dieser Zeit kein Event liegt.
  const calendarVisibleStartMin = timeSlotLayout
    ? Math.min(
      timeSlotLayout.visibleStartMin,
      labelStartMin === null ? timeSlotLayout.visibleStartMin : Math.floor(Math.max(0, labelStartMin) / 60) * 60
    )
    : 0;
  const calendarVisibleEndMin = timeSlotLayout
    ? Math.max(
      timeSlotLayout.visibleEndMin,
      labelEndMin === null ? timeSlotLayout.visibleEndMin : Math.ceil(Math.min(1440, labelEndMin) / 60) * 60
    )
    : 0;
  const calendarHeight = timeSlotLayout
    ? Math.max(240, (calendarVisibleEndMin - calendarVisibleStartMin) * calendarPixelsPerMinute)
    : 0;

  const timeAxisMarks = useMemo(() => {
    if (!timeSlotLayout) return [] as { min: number; isHour: boolean }[];
    const start = calendarVisibleStartMin;
    const end = calendarVisibleEndMin;
    if (!isFinite(start) || !isFinite(end)) return [];

    const marks: { min: number; isHour: boolean }[] = [];
    for (let min = start; min <= end; min += 15) {
      marks.push({ min, isHour: min % 60 === 0 });
    }
    if (marks[marks.length - 1]?.min !== end) {
      marks.push({ min: end, isHour: end % 60 === 0 });
    }
    return marks;
  }, [calendarVisibleEndMin, calendarVisibleStartMin, timeSlotLayout]);

  useEffect(() => {
    if (selected?.type !== "calendarEvent") return;
    const exists = filteredRows.some((row) => row.__rowIndex === selected.rowIndex);
    if (!exists) {
      setSelected(null);
    }
  }, [filteredRows, selected]);

  /**
   * getCellContent:
   * Liefert den JSX-Inhalt für eine Zelle in der Tabelle.
   * - wenn keine Treffer: null
   * - bei mehreren Treffern: Anzeige der Anzahl (multi-count)
   * - bei einem Treffer: Darstellung der Werte anderer Dimensionen (otherDims),
   *   optional ohne die aktuell selektierte Point-Dimension.
   */
  const getCellContent = (
    weekday: string,
    phase: string,
    matching: PlanRow[]
  ) => {
    if (matching.length === 0) return null;

    const cellKey = `${weekday}|${phase}`;

    const renderArrows = () => (
      <>
        <button
          type="button"
          className="cell-pager-arrow cell-pager-arrow--left"
          title="Vorheriger Eintrag"
          aria-label="Vorheriger Eintrag"
          onClick={(e) => {
            e.stopPropagation();
            setCellPagerOpenByKey((prev) => ({ ...prev, [cellKey]: true }));
            setCellEntryIndexByKey((prev) => {
              const prevIndex = prev[cellKey] ?? 0;
              const nextIndex = (prevIndex - 1 + matching.length) % matching.length;
              return { ...prev, [cellKey]: nextIndex };
            });
          }}
        >
          ‹
        </button>
        <button
          type="button"
          className="cell-pager-arrow cell-pager-arrow--right"
          title="Nächster Eintrag"
          aria-label="Nächster Eintrag"
          onClick={(e) => {
            e.stopPropagation();
            setCellPagerOpenByKey((prev) => ({ ...prev, [cellKey]: true }));
            setCellEntryIndexByKey((prev) => {
              const prevIndex = prev[cellKey] ?? 0;
              const nextIndex = (prevIndex + 1) % matching.length;
              return { ...prev, [cellKey]: nextIndex };
            });
          }}
        >
          ›
        </button>
      </>
    );

    // Verhalten wie vorher: bei mehreren Einträgen erstmal nur die Anzahl zeigen.
    // Erst wenn der User die Zelle "öffnet" (Klick), zeigen wir Details + Pfeile.
    if (matching.length > 1 && !cellPagerOpenByKey[cellKey]) {
      return (
        <>
          {renderArrows()}
          <div className="multi-count">{matching.length}</div>
        </>
      );
    }

    const currentIndexRaw = cellEntryIndexByKey[cellKey] ?? 0;
    const currentIndex = Math.max(0, Math.min(currentIndexRaw, matching.length - 1));
    const row = matching[currentIndex];
    const displayValues = getRowDisplayValues(row);

    return (
      <>
        {matching.length > 1 ? renderArrows() : null}

        {displayValues.length > 0
          ? displayValues.map((value, idx) => (
            <div key={`${cellKey}-${idx}`}>{value}</div>
          ))
          : <div className="multi-count">1</div>}
      </>
    );
  };

  const renderTimeSlotCalendar = () => {
    if (!weekdayDim || !phaseDim || phaseDim.dimType !== "time.slot" || !timeSlotLayout) {
      return null;
    }

    const calendarColumnCount = Math.max(displayWdPoints.length, 1);
    const calendarTagAxisWidth = 72;
    const calendarLeftColumns = `${hasPhTagGroups ? `var(--time-slot-tag-axis-width, ${calendarTagAxisWidth}px) ` : ""}var(--time-slot-axis-width, ${calendarTimeAxisWidth}px)`;
    const calendarGridColumns = `${calendarLeftColumns} repeat(${calendarColumnCount}, minmax(0, 1fr))`;
    const dayHeaderStartColumn = hasPhTagGroups ? 3 : 2;

    return (
      <div
        className="time-slot-calendar"
        style={{
          "--time-slot-axis-width": `${calendarTimeAxisWidth}px`,
          "--time-slot-tag-axis-width": `${calendarTagAxisWidth}px`,
        } as React.CSSProperties}
      >
        <div className="time-slot-calendar__toolbar" style={{ gridTemplateColumns: calendarGridColumns }}>
          <div
            className="dimension-heading hue-fg time-slot-calendar__axis-heading"
            style={{ gridColumn: hasPhTagGroups ? "1 / span 2" : "1" }}
            draggable={!!phaseDim}
            onDragStart={(e) => {
              if (!phaseDim) return;
              e.dataTransfer.effectAllowed = "copy";
              e.dataTransfer.setData(
                "application/x-zeitgeist",
                JSON.stringify({
                  kind: "create-condition-from-dimension",
                  dimensionId: phaseDim.id,
                  dimensionName: phName,
                })
              );
            }}
            onDragOver={(e) => {
              if (!draggedDimId) return;
              e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleAxisDrop(1);
            }}
          >
            {phName}
          </div>
          <div
            className="dimension-heading hue-fg time-slot-calendar__axis-heading time-slot-calendar__axis-heading--columns"
            style={{ gridColumn: `${dayHeaderStartColumn} / -1` }}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "copy";
              e.dataTransfer.setData(
                "application/x-zeitgeist",
                JSON.stringify({
                  kind: "create-condition-from-dimension",
                  dimensionId: weekdayDim.id,
                  dimensionName: wdName,
                })
              );
            }}
            onDragOver={(e) => {
              if (!draggedDimId) return;
              e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleAxisDrop(0);
            }}
          >
            {wdName}
          </div>
        </div>

        <div className="time-slot-calendar__scroll">
          {hasWdTagGroups && (
            <div className="time-slot-calendar__tag-grid" style={{ gridTemplateColumns: calendarGridColumns }}>
              {hasPhTagGroups && <div className="time-slot-calendar__tag-axis-spacer" />}
              <div className="time-slot-calendar__tag-spacer" />
              <div className="time-slot-calendar__tag-lens" style={{ gridColumn: `${dayHeaderStartColumn} / -1` }}>
                {renderWeekdayTagLens(calendarColumnCount)}
              </div>
            </div>
          )}

          <div className="time-slot-calendar__grid" style={{ gridTemplateColumns: calendarGridColumns }}>
            {hasPhTagGroups && <div className="time-slot-calendar__tag-axis-spacer" />}
            <div className="time-slot-calendar__time-axis-spacer" />

            {displayWdPoints.map((point) => {
              const isSelected =
                selected?.type === "weekdayPoint" && selected.id === point.id;

              return (
                <div
                  key={point.id}
                  className={`clickable weekday-point hue-fg time-slot-calendar__day-header ${isSelected ? "selected" : ""}`}
                  draggable
                  onDragStart={(e) => {
                    suppressSelectionClickShortly();
                    e.dataTransfer.effectAllowed = "copy";
                    e.dataTransfer.setData(
                      "application/x-zeitgeist",
                      JSON.stringify({
                        kind: "create-condition-from-dimension-point",
                        dimensionId: weekdayDim.id,
                        dimensionName: wdName,
                        pointId: point.id,
                        pointName: point.name,
                      })
                    );
                  }}
                  onClick={() => {
                    if (shouldIgnoreSelectionClick()) return;
                    setSelected(
                      isSelected
                        ? null
                        : {
                          type: "weekdayPoint",
                          dimId: weekdayDim.id,
                          id: point.id,
                          name: point.name,
                        }
                    );
                    if (projectId) notifyProjectActivity(projectId).catch(() => { });
                  }}
                >
                  {point.name}
                </div>
              );
            })}

            {hasPhTagGroups && (
              <div className="time-slot-calendar__slot-tag-axis" style={{ height: calendarHeight }}>
                <div className="axis-tag-stack axis-tag-stack--y-list">
                  {phAxisTags
                    .filter((tag) => !phTagFilter || tag.id === phTagFilter)
                    .map((tag) => (
                      <React.Fragment key={tag.id}>
                        {renderPhaseTagChip(tag.id, tag.name)}
                      </React.Fragment>
                    ))}
                </div>
              </div>
            )}

            <div className="time-slot-calendar__time-axis" style={{ height: calendarHeight, "--ppm": calendarPixelsPerMinute } as React.CSSProperties}>
              {timeAxisMarks.map(({ min: markMin, isHour }) => {
                const top = (markMin - calendarVisibleStartMin) * calendarPixelsPerMinute;
                return (
                  <div
                    key={`axis-${markMin}`}
                    className={`time-slot-calendar__time-mark${isHour ? "" : " time-slot-calendar__time-mark--quarter"}`}
                    style={{ top }}
                  >
                    {isHour && <span>{formatMinutes(markMin)}</span>}
                  </div>
                );
              })}

              {/* time.slot-Points auf Höhe ihrer start_time: sichtbar und per Drag&Drop nutzbar */}
              {timeSlotPointLabels.map(({ point, startMin }) => {
                const top = (startMin - calendarVisibleStartMin) * calendarPixelsPerMinute;
                const isSelected =
                  selected?.type === "phasePoint" && selected.id === point.id;

                return (
                  <button
                    key={`slot-label-${point.id}`}
                    type="button"
                    className={`time-slot-calendar__slot-label phase-point hue-fg ${isSelected ? "selected" : ""}`}
                    style={{ top }}
                    draggable
                    onDragStart={(e) => {
                      suppressSelectionClickShortly();
                      e.dataTransfer.effectAllowed = "copy";
                      e.dataTransfer.setData(
                        "application/x-zeitgeist",
                        JSON.stringify({
                          kind: "create-condition-from-dimension-point",
                          dimensionId: phaseDim.id,
                          dimensionName: phName,
                          pointId: point.id,
                          pointName: point.name,
                        })
                      );
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (shouldIgnoreSelectionClick()) return;
                      setSelected(
                        isSelected
                          ? null
                          : {
                            type: "phasePoint",
                            dimId: phaseDim.id,
                            id: point.id,
                            name: point.name,
                          }
                      );
                      if (projectId) notifyProjectActivity(projectId).catch(() => { });
                    }}
                  >
                    {point.name}
                  </button>
                );
              })}
            </div>

            {displayWdPoints.map((weekday, columnIndex) => {
              const segments = timeSlotSegmentsByColumn.get(columnIndex) ?? [];
              const overflowGroups = timeSlotOverflowGroupsByColumn.get(columnIndex) ?? [];

              return (
                <div key={`${weekday.id}-calendar`} className="time-slot-calendar__day-column">
                  <div className="time-slot-calendar__day-grid" style={{ height: calendarHeight, "--ppm": calendarPixelsPerMinute } as React.CSSProperties}>
                    {segments.map((segment) => {
                      const visibleLaneCount = Math.min(segment.laneCount, calendarVisibleLaneCap);
                      const top = (segment.startMin - calendarVisibleStartMin) * calendarPixelsPerMinute;
                      const height = Math.max(
                        calendarMinBlockHeight,
                        (segment.endMin - segment.startMin) * calendarPixelsPerMinute
                      );
                      const leftPct = (segment.laneIndex / visibleLaneCount) * 100;
                      const widthPct = 100 / visibleLaneCount;
                      const displayValues = getRowDisplayValues(segment.row);
                      const primaryLabel = displayValues[0] ?? segment.phaseName;
                      const secondaryLines = displayValues.slice(1, 3);
                      const isSelected =
                        selected?.type === "calendarEvent" && selected.eventId === segment.eventId;
                      const timeLabel = `${formatMinutes(segment.startMin)} - ${formatMinutes(segment.endMin)}`;
                      const title =
                        displayValues.length > 0
                          ? `${timeLabel} | ${displayValues.join(" | ")}`
                          : `${timeLabel} | ${segment.phaseName}`;

                      // Zeige Overflow-Zahl auf der letzten sichtbaren Karte
                      const overflowGroupForSegment = overflowGroups.find(
                        (g) => g.startMin === segment.startMin && segment.laneIndex === visibleLaneCount - 1
                      );
                      const totalCount = overflowGroupForSegment
                        ? overflowGroupForSegment.hiddenCount
                        : null;

                      return (
                        <button
                          key={segment.id}
                          type="button"
                          title={title}
                          className={`time-slot-event${isSelected ? " selected" : ""}${segment.continuesFromPrev ? " continues-from-prev" : ""}${segment.continuesToNext ? " continues-to-next" : ""}`}
                          style={{
                            top,
                            height,
                            left: `calc(${leftPct}% + 3px)`,
                            width: `calc(${widthPct}% - 6px)`,
                            "--lesson-bg": getLessonBg(data, segment.row),
                          } as React.CSSProperties}
                          onMouseEnter={(e) => {
                            if (document.body.classList.contains("is-dragging")) return;
                            if (!overflowGroupForSegment) return;
                            const key = overflowGroupForSegment.id;
                            if (hoverTimerByKey.current[key]) {
                              clearTimeout(hoverTimerByKey.current[key]!);
                              hoverTimerByKey.current[key] = null;
                            }
                            // Bereits via longpress offen → kein hover-Timer nötig
                            if (openCalendarOverflowGroupId === key) return;
                            const el = e.currentTarget as HTMLElement;
                            hoverTimerByKey.current[key] = window.setTimeout(() => {
                              hoverTimerByKey.current[key] = null;
                              if (document.body.classList.contains("is-dragging")) return;
                              const rect = el.getBoundingClientRect();
                              setCellListOpenMethod((prev) => ({ ...prev, [key]: "hover" }));
                              setOpenCalendarOverflowGroupId(key);
                              setCalendarOverflowPortalRect({
                                top: rect.bottom + 4,
                                left: rect.left,
                                width: Math.max(rect.width, 220),
                              });
                            }, 3000);
                          }}
                          onMouseLeave={() => {
                            if (!overflowGroupForSegment) return;
                            const key = overflowGroupForSegment.id;
                            if (hoverTimerByKey.current[key]) {
                              clearTimeout(hoverTimerByKey.current[key]!);
                              hoverTimerByKey.current[key] = null;
                            }
                            // Nur schließen wenn per hover geöffnet (longpress bleibt offen)
                            if (cellListOpenMethod[key] === "hover") {
                              setTimeout(() => {
                                setOpenCalendarOverflowGroupId((cur) => cur === key ? null : cur);
                                setCalendarOverflowPortalRect((cur) => cur !== null && openCalendarOverflowGroupId === key ? null : cur);
                                setCellListOpenMethod((prev) => { const n = { ...prev }; delete n[key]; return n; });
                              }, 120);
                            }
                          }}
                          onPointerDown={(e) => {
                            if (!overflowGroupForSegment) return;
                            if (e.button !== 0) return;
                            const key = overflowGroupForSegment.id;
                            const lastOpen = longPressTimerByKey.current['__lastOpen'] || 0;
                            if (Date.now() - lastOpen < 400) return;
                            
                            const lastUp = longPressTimerByKey.current['__lastUp'] || 0;
                            if (Date.now() - lastUp < 400) return;

                            isLongPressTriggered.current = false;
                            // Hover-Timer stoppen damit er nicht vor Long-Press feuert
                            if (hoverTimerByKey.current[key]) {
                              clearTimeout(hoverTimerByKey.current[key]!);
                              hoverTimerByKey.current[key] = null;
                            }
                            if (longPressTimerByKey.current[key]) {
                              clearTimeout(longPressTimerByKey.current[key]!);
                            }
                            const el = e.currentTarget as HTMLElement;
                            longPressTimerByKey.current[key] = window.setTimeout(() => {
                              longPressTimerByKey.current[key] = null;
                              longPressTimerByKey.current['__lastOpen'] = Date.now();
                              isLongPressTriggered.current = true;
                              const rect = el.getBoundingClientRect();
                              setCellListOpenMethod((prev) => ({ ...prev, [key]: "longpress" }));
                              setOpenCalendarOverflowGroupId(key);
                              setCalendarOverflowPortalRect({
                                top: rect.bottom + 4,
                                left: rect.left,
                                width: Math.max(rect.width, 280),
                              });
                            }, 600);
                          }}
                          onPointerUp={() => {
                            if (!overflowGroupForSegment) return;
                            longPressTimerByKey.current['__lastUp'] = Date.now();
                            setTimeout(() => { isLongPressTriggered.current = false; }, 500);
                            const lpKey = overflowGroupForSegment.id;
                            if (longPressTimerByKey.current[lpKey]) {
                              clearTimeout(longPressTimerByKey.current[lpKey]!);
                              longPressTimerByKey.current[lpKey] = null;
                            }
                          }}
                          onPointerCancel={() => {
                            if (!overflowGroupForSegment) return;
                            longPressTimerByKey.current['__lastUp'] = Date.now();
                            setTimeout(() => { isLongPressTriggered.current = false; }, 500);
                            const lpKey = overflowGroupForSegment.id;
                            if (longPressTimerByKey.current[lpKey]) {
                              clearTimeout(longPressTimerByKey.current[lpKey]!);
                              longPressTimerByKey.current[lpKey] = null;
                            }
                          }}
                          onClick={(e) => {
                            // Long-Press hat getriggert → Click ignorieren
                            if (isLongPressTriggered.current) {
                              isLongPressTriggered.current = false;
                              e.stopPropagation();
                              return;
                            }
                            // Normaler Click → Ereignis selektieren (kein manuelles Toggle mehr)
                            setSelected(
                              isSelected
                                ? null
                                : {
                                  type: "calendarEvent",
                                  eventId: segment.eventId,
                                  rowIndex: segment.row.__rowIndex ?? segment.eventId,
                                  weekdayDimId: weekdayDim.id,
                                  phaseDimId: phaseDim.id,
                                  weekday: weekday.name,
                                  phase: segment.phaseName,
                                }
                            );
                            if (projectId) notifyProjectActivity(projectId).catch(() => { });
                          }}
                        >
                          <span className="time-slot-event__title">{primaryLabel}</span>
                          {secondaryLines.map((line, idx) => (
                            <span key={`${segment.id}-line-${idx}`} className="time-slot-event__meta">
                              {line}
                            </span>
                          ))}
                          {totalCount !== null && (
                            <span className="time-slot-event__overflow-count">+{totalCount}</span>
                          )}
                        </button>
                      );
                    })}

                    {/* Fallback-Badge nur für Overflow-Gruppen ohne sichtbare letzte Karte */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    );
  };

  return (
    <>
      <SuperFilterStyles />
      {/* Header-Bereich mit Logout, User-Info, Admin-Link und Forum-Link */}
      <div className="plan-header">
        {/* Links: Admin-Toggle + User-Info */}
        <div className="header-left" style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            {/* Admin-Toggle */}
            {onToggleAdmin && (
              <button
                type="button"
                className="header-ghost-btn"
                onClick={onToggleAdmin}
                title={adminCollapsed ? "Administration einblenden" : "Administration ausblenden"}
                aria-label={adminCollapsed ? "Administration einblenden" : "Administration ausblenden"}
                aria-pressed={!adminCollapsed}
              >
                {"Admin"}
              </button>
            )}

            <ProjectOpenDropdown user={user} onProjectChange={onProjectChange} currentProjectId={projectId} />

          </div>
        </div>

        {/* Plan-Titel aus den Settings + Status */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <h1 className="header-title">{projectName}</h1>
          {schedulerStatus && schedulerStatus !== "done" && schedulerStatus !== "?" && (
            <div className="status-indicator">
              {schedulerStatus === "solving" && (
                <div
                  className="status-dot pulsing"
                  style={{
                    backgroundColor: `oklch(0.60 0.15 ${data.settings?.hue ?? 172})`
                  }}
                />
              )}
              <span
                style={{
                  color: `oklch(0.60 0.15 ${data.settings?.hue ?? 172})`,
                  fontFamily: "monospace",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  marginLeft: schedulerStatus === "solving" ? 0 : 4
                }}
              >
                {schedulerStatus === "solving" ? (
                  Array.from("solving").map((char, i) => (
                    <span
                      key={i}
                      className="anim-letter"
                      style={{ animationDelay: `${i * 0.1}s`, display: "inline-block" }}
                    >
                      {char}
                    </span>
                  ))
                ) : (
                  schedulerStatus
                )}
              </span>
            </div>
          )}
        </div>

        {/* Rechts: Forum + Profile */}
        <div className="header-right" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ProfileDropdown user={user} onLogout={onLogout} />
        </div>
      </div>

      {/* Hauptinhalt: Sidebar mit Dimensionen und Plan-Tabelle */}
      {/* ── DB nicht erreichbar ── */}
      {dbDown && (
        <div style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
        }}>
          <div style={{
            background: "var(--bg, #fff)",
            border: "1.5px solid rgba(255,65,108,0.35)",
            borderRadius: 20,
            padding: "48px 56px",
            textAlign: "center",
            maxWidth: 420,
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
          }}>
            {/* Pulsierendes Icon */}
            <div style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ff416c, #ff4b2b)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              fontSize: 28,
              animation: "db-pulse 2s ease-in-out infinite",
            }}>
              🗄️
            </div>
            <h2 style={{
              margin: "0 0 10px",
              fontSize: 18,
              fontWeight: 700,
              color: "var(--text-color, #111)",
            }}>
              Verbindung unterbrochen
            </h2>
            <p style={{
              margin: "0 0 28px",
              fontSize: 14,
              lineHeight: 1.65,
              color: "var(--text-muted, #666)",
            }}>
              DB derzeit nicht erreichbar.<br />Versuchen Sie es später erneut.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 28px",
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(102,126,234,0.4)",
                transition: "opacity 0.2s, transform 0.2s",
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}
            >
              Erneut versuchen
            </button>
          </div>
          <style>{`
            @keyframes db-pulse {
              0%   { box-shadow: 0 0 0 0   rgba(255,65,108,0.5); }
              70%  { box-shadow: 0 0 0 16px rgba(255,65,108,0); }
              100% { box-shadow: 0 0 0 0   rgba(255,65,108,0); }
            }
          `}</style>
        </div>
      )}

      {/* Constraint: Dimensions never takes more than 50% of the Plan view */}
      {!dbDown && (
        <div
          className="plan-content"
          style={{ gridTemplateColumns: sidebarDims.length > 0 ? `min(${dimsWidth}px, 50%) 8px minmax(0, 1fr)` : `1fr`, gap: 0 }}
          onDragStartCapture={setDetachedDragImage}
        >
          {/* Dimensionen – inkl. Drag & Drop Sortierung wie in Version 0.4 */}
          {sidebarDims.length > 0 && (
            <>
              <div
                className="dimensions-row"
                onDragOver={(event) => {
                  // Nur erlauben, wenn tatsächlich eine Dimension gezogen wird
                  if (!draggedDimId) return;
                  event.preventDefault();
                }}
                onDrop={(event) => {
                  // Drop auf den leeren Bereich der Dimensionen-Leiste
                  // Aktuell: keine spezielle Aktion (nur Drag-Abbruch)
                  if (!draggedDimId) return;
                  event.preventDefault();
                  setDraggedDimId(null);
                }}
              >
                {sidebarDims.map((dim) => {
                  // Assoziierte Punktnamen dieser Dimension (abhängig von rowsForAssociations)
                  const assocNames = associatedNamesByDim.get(dim.id) || new Set<string>();

                  // Achsen-Dimensionen optisch markieren
                  const isAxisDim = dim.id === weekdayDim?.id || dim.id === phaseDim?.id;
                  const axisSuffix =
                    dim.id === weekdayDim?.id
                      ? " (Spalte)"
                      : dim.id === phaseDim?.id
                        ? " (Zeile)"
                        : "";

                  const sfHue = data.settings?.hue ?? 172;
                  const sfContrastHue = (sfHue + 240) % 360;

                  return (
                    <div
                      key={dim.id}
                      className={`dimension-box${isAxisDim ? " axis-dimension" : ""}`}
                      style={{ "--superfilter-color": `oklch(0.55 0.20 ${sfContrastHue})` } as React.CSSProperties}
                    >
                      {/* Header: Titel + Superfilter Checkbox */}
                      <div className="dimension-title-row">
                        {/* Titel einer Dimension: draggable für Sortierung und clickable für Selektion */}
                        <h3
                          className={`clickable dimension-title ${!superFilterEnabledByDimId[dim.id] ? "hue-fg " : ""}${selected?.type === "dimension" && selected?.dimId === dim.id
                            ? "selected"
                            : ""
                            }`}
                          draggable
                          onDragStart={(event) => {
                            suppressSelectionClickShortly();
                            setDraggedDimId(dim.id);
                            event.dataTransfer.effectAllowed = "copyMove";
                            event.dataTransfer.setData("text/plain", dim.id);
                            event.dataTransfer.setData(
                              "application/x-zeitgeist",
                              JSON.stringify({
                                kind: "create-condition-from-dimension",
                                dimensionId: dim.id,
                                dimensionName: dim.name,
                              })
                            );
                          }}
                          onDragOver={(event) => {
                            if (!draggedDimId || draggedDimId === dim.id) return;
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                            event.stopPropagation();
                          }}
                          onDragEnter={(event) => {
                            // Beim Überfahren eine Vorschau-Sortierung auslösen
                            if (!draggedDimId || draggedDimId === dim.id) return;
                            event.preventDefault();
                            event.stopPropagation();
                            insertBeforeTarget(dim.id);
                          }}
                          onDrop={(event) => {
                            // Drop direkt auf dieser Dimension
                            event.preventDefault();
                            event.stopPropagation();
                            handleDimDrop(dim.id);
                          }}
                          onDragEnd={() => {
                            suppressSelectionClickShortly();
                            setDraggedDimId(null);
                          }}
                          onClick={() => {
                            // Klick selektiert oder deselektiert die Dimension
                            if (shouldIgnoreSelectionClick()) return;
                            if (draggedDimId) return; // keine Selektion ändern, wenn gerade Drag aktiv
                            if (superFilterEnabledByDimId[dim.id]) return; // Keine Selektion der Dimension, wenn Superfilter aktiv
                            setSelected(
                              selected?.type === "dimension" && selected.dimId === dim.id
                                ? null
                                : { type: "dimension", dimId: dim.id }
                            );
                            // Activity-Tracking
                            if (projectId) notifyProjectActivity(projectId).catch(() => { });
                          }}
                        >
                          {dim.name}
                          {axisSuffix}
                          <button
                            className={`superfilter-toggle${superFilterEnabledByDimId[dim.id] ? " superfilter-toggle--active" : ""}`}
                            title="Superfilter für diese Dimension aktivieren"
                            style={superFilterEnabledByDimId[dim.id]
                              ? { background: `oklch(0.72 0.18 ${sfContrastHue})`, borderColor: `oklch(0.58 0.20 ${sfContrastHue})` }
                              : undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSuperFilterForDim(dim.id);
                              if (projectId) notifyProjectActivity(projectId).catch(() => { });
                            }}
                          />
                        </h3>
                      </div>

                      {/* Scrollbarer Bereich für Tags und Punkte der Dimension */}
                      <div className="dimension-scroll">
                        {/* Tags der Dimension */}
                        {dim.tags.map((tag) => {
                          // Tag ist assoziiert, wenn einer seiner Punkte in assocNames vorkommt
                          const tagAssociated = dim.points.some(
                            (p) => p.tagRefs.includes(tag.id) && assocNames.has(p.name)
                          );
                          const tagSelected =
                            !superFilterEnabledByDimId[dim.id] &&
                            selected?.type === "tag" &&
                            selected?.tagId === tag.id &&
                            selected.dimId === dim.id;

                          const tagSuperSelected = (superFilterItemsByDimId[dim.id] ?? []).some(
                            (x) => x.kind === "tag" && x.id === tag.id
                          );

                          return (
                            <div
                              key={tag.id}
                              data-sf-dim={dim.id}
                              data-sf-kind="tag"
                              data-sf-id={tag.id}
                              className={
                                "clickable" +
                                (tagSelected ? " selected" : (!superFilterEnabledByDimId[dim.id] && tagAssociated) ? " associated" : "") +
                                (superFilterEnabledByDimId[dim.id] && tagSuperSelected ? " superfilter-selected" : "")
                              }
                              draggable={!superFilterEnabledByDimId[dim.id] || !tagSuperSelected}
                              onDragStart={(e: React.DragEvent) => {
                                if (superFilterEnabledByDimId[dim.id] && tagSuperSelected) { e.preventDefault(); return; }
                                suppressSelectionClickShortly();
                                e.dataTransfer.effectAllowed = "copy";
                                e.dataTransfer.setData(
                                  "application/x-zeitgeist",
                                  JSON.stringify({
                                    kind: "create-condition-from-dimension-tag",
                                    dimensionId: dim.id,
                                    dimensionName: dim.name,
                                    tagId: tag.id,
                                    tagName: tag.name,
                                  })
                                );
                              }}
                              onDragEnd={() => suppressSelectionClickShortly()}
                              onPointerDown={(e) => {
                                if (superFilterEnabledByDimId[dim.id] && tagSuperSelected) {
                                  handleSfPointerDown(e, dim.id);
                                }
                              }}
                              onPointerMove={handleSfPointerMove}
                              onPointerUp={(e) => handleSfPointerUp(e)}
                              onPointerCancel={(e) => handleSfPointerUp(e)}
                              style={superFilterEnabledByDimId[dim.id] && tagSuperSelected ? { cursor: "grab", touchAction: "none" } : undefined}
                              onClick={() => {
                                if (shouldIgnoreSelectionClick()) return;
                                if (sfDragActive.current) return;
                                // Wenn Superfilter aktiv ist: Klick machen wir nichts mehr,
                                // weil die Auswahl NUR noch per Drag verschoben werden soll.
                                if (superFilterEnabledByDimId[dim.id]) {
                                  return;
                                } else {
                                  setSelected(
                                    tagSelected
                                      ? null
                                      : { type: "tag", dimId: dim.id, tagId: tag.id }
                                  );
                                }
                                // Activity-Tracking
                                if (projectId) notifyProjectActivity(projectId).catch(() => { });
                              }}
                            >
                              <strong>{tag.name}</strong>
                            </div>
                          );
                        })}

                        {/* Punkte der Dimension */}
                        {dim.points.map((p) => {
                          const pointAssociated = assocNames.has(p.name);
                          const pointSelected =
                            !superFilterEnabledByDimId[dim.id] &&
                            selected?.type === "point" && selected?.pointId === p.id;

                          const pointSuperSelected = (superFilterItemsByDimId[dim.id] ?? []).some(
                            (x) => x.kind === "point" && x.id === p.id
                          );

                          return (
                            <div
                              key={p.id}
                              data-sf-dim={dim.id}
                              data-sf-kind="point"
                              data-sf-id={p.id}
                              className={
                                "clickable" +
                                (pointSelected ? " selected" : (!superFilterEnabledByDimId[dim.id] && pointAssociated) ? " associated" : "")
                                + (superFilterEnabledByDimId[dim.id] && pointSuperSelected ? " superfilter-selected" : "")
                              }
                              draggable={!superFilterEnabledByDimId[dim.id] || !pointSuperSelected}
                              onDragStart={(e: React.DragEvent) => {
                                if (superFilterEnabledByDimId[dim.id] && pointSuperSelected) { e.preventDefault(); return; }
                                suppressSelectionClickShortly();
                                e.dataTransfer.effectAllowed = "copy";
                                e.dataTransfer.setData(
                                  "application/x-zeitgeist",
                                  JSON.stringify({
                                    kind: "create-condition-from-dimension-point",
                                    dimensionId: dim.id,
                                    dimensionName: dim.name,
                                    pointId: p.id,
                                    pointName: p.name,
                                  })
                                );
                              }}
                              onDragEnd={() => suppressSelectionClickShortly()}
                              onPointerDown={(e) => {
                                if (superFilterEnabledByDimId[dim.id] && pointSuperSelected) {
                                  handleSfPointerDown(e, dim.id);
                                }
                              }}
                              onPointerMove={handleSfPointerMove}
                              onPointerUp={(e) => handleSfPointerUp(e)}
                              onPointerCancel={(e) => handleSfPointerUp(e)}
                              style={superFilterEnabledByDimId[dim.id] && pointSuperSelected ? { cursor: "grab", touchAction: "none" } : undefined}
                              onClick={() => {
                                if (shouldIgnoreSelectionClick()) return;
                                if (sfDragActive.current) return;
                                // Wenn Superfilter aktiv ist: Klick machen wir nichts mehr,
                                // weil die Auswahl NUR noch per Drag verschoben werden soll.
                                if (superFilterEnabledByDimId[dim.id]) {
                                  return;
                                } else {
                                  setSelected(
                                    pointSelected
                                      ? null
                                      : { type: "point", dimId: dim.id, pointId: p.id }
                                  );
                                }
                                // Activity-Tracking
                                if (projectId) notifyProjectActivity(projectId).catch(() => { });
                              }}
                            >
                              {p.name}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Gutter for resizing */}
              <div
                className="gutter gutter-inner"
                onMouseDown={startResizing}
                title="Drag to resize dimensions"
              />
            </>
          )}

          {/* Tabelle + (optional) Statusbereich */}
          <div className="table-and-status">
            <div className="plan-table-container">
              {/* Tabelle nur anzeigen, wenn mindestens eine Achsendimension vorhanden ist */}
              {weekdayDim ? (
                phaseDim?.dimType === "time.slot" ? (
                  renderTimeSlotCalendar()
                ) : (
                  <table className="plan-table">
                    {hasPhTagGroups && (
                      <colgroup>
                        <col className="plan-table__y-tag-col" />
                        <col span={Math.max(1, displayWdPoints.length) + 1} />
                      </colgroup>
                    )}
                    <thead>
                    {/* Erste Kopfzeile: Namen der Achsendimensionen, Drop-Ziele für Drag & Drop */}
                    <tr>
                      <th
                        className="dimension-heading hue-fg"
                        colSpan={hasPhTagGroups ? 2 : 1}
                        draggable={!!phaseDim}
                        onDragStart={(e) => {
                          if (!phaseDim) return;
                          e.dataTransfer.effectAllowed = "copy";
                          e.dataTransfer.setData(
                            "application/x-zeitgeist",
                            JSON.stringify({
                              kind: "create-condition-from-dimension",
                              dimensionId: phaseDim.id,
                              dimensionName: phName,
                            })
                          );
                        }}
                        onDragOver={(e) => {
                          if (!draggedDimId) return;
                          e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleAxisDrop(1); // 1 = Phase / Zeile
                        }}
                      >
                        {phName}
                      </th>
                      <th
                        className="dimension-heading hue-fg"
                        colSpan={Math.max(1, displayWdPoints.length)}
                        style={{ textAlign: "center" }}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "copy";
                          e.dataTransfer.setData(
                            "application/x-zeitgeist",
                            JSON.stringify({
                              kind: "create-condition-from-dimension",
                              dimensionId: weekdayDim.id,
                              dimensionName: wdName,
                            })
                          );
                        }}
                        onDragOver={(e) => {
                          if (!draggedDimId) return;
                          e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleAxisDrop(0); // 0 = Weekday / Spalte
                        }}
                      >
                        {wdName}
                      </th>
                    </tr>

                    {/* X-Achse Tags: ein Overlay-Row; überlappende Tags teilen sich Punkte */}
                    {hasWdTagGroups && (
                      <tr className="axis-tags-row axis-tags-row--lens">
                        <th colSpan={hasPhTagGroups ? 2 : 1} />
                        <th colSpan={Math.max(1, displayWdPoints.length)} className="axis-tag-lens-th">
                          {renderWeekdayTagLens(displayWdPoints.length)}
                        </th>
                      </tr>
                    )}

                    {/* Zweite Kopfzeile: einzelne Punkte der Spalten-Dimension (z. B. Mo–Fr) */}
                    <tr>
                      <th colSpan={hasPhTagGroups ? 2 : 1} />
                      {displayWdPoints.map((p) => {
                        const isSelected =
                          selected?.type === "weekdayPoint" && selected.id === p.id;
                        return (
                          <th
                            key={p.id}
                            className={`clickable weekday-point hue-fg ${isSelected ? "selected" : ""
                              }`}
                            draggable
                            onDragStart={(e) => {
                              suppressSelectionClickShortly();
                              e.dataTransfer.effectAllowed = "copy";
                              e.dataTransfer.setData(
                                "application/x-zeitgeist",
                                JSON.stringify({
                                  kind: "create-condition-from-dimension-point",
                                  dimensionId: weekdayDim.id,
                                  dimensionName: wdName,
                                  pointId: p.id,
                                  pointName: p.name,
                                })
                              );
                            }}
                            onClick={() => {
                              if (shouldIgnoreSelectionClick()) return;
                              setSelected(
                                isSelected
                                  ? null
                                  : {
                                    type: "weekdayPoint",
                                    dimId: weekdayDim.id,
                                    id: p.id,
                                    name: p.name,
                                  }
                              );
                              // Activity-Tracking
                              if (projectId) notifyProjectActivity(projectId).catch(() => { });
                            }}
                          >
                            {p.name}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {/* Für jeden Punkt der Zeilen-Dimension. Dummy-Zeile, wenn es noch keine zweite Dimension gibt */}
                    {(phaseDim && displayPhPoints.length > 0 ? displayPhPoints : [{ id: "dummy_row", name: "" }]).map((phase, phaseIndex, phaseRows) => {
                      const rowSelected =
                        phaseDim && selected?.type === "phasePoint" && selected.id === phase.id;

                      return (
                        <tr key={phase.id}>
                          {hasPhTagGroups && phaseIndex === 0 && (
                            <th
                              className="axis-tag-point-th axis-tag-group-th axis-tag-group-th--row axis-tag-group-th--tag-list"
                              rowSpan={phaseRows.length}
                            >
                              <div className="axis-tag-stack axis-tag-stack--y-list">
                                {phAxisTags
                                  .filter((tag) => !phTagFilter || tag.id === phTagFilter)
                                  .map((tag) => (
                                    <React.Fragment key={tag.id}>
                                      {renderPhaseTagChip(tag.id, tag.name)}
                                    </React.Fragment>
                                  ))}
                              </div>
                            </th>
                          )}
                          {/* Zeilenüberschrift (Phase-Punkt) */}
                          <th
                            scope="row"
                            className={`clickable phase-point hue-fg ${rowSelected ? "selected" : ""
                              }`}
                            draggable={!!phaseDim}
                            onDragStart={(e) => {
                              if (!phaseDim) return;
                              suppressSelectionClickShortly();
                              e.dataTransfer.effectAllowed = "copy";
                              e.dataTransfer.setData(
                                "application/x-zeitgeist",
                                JSON.stringify({
                                  kind: "create-condition-from-dimension-point",
                                  dimensionId: phaseDim.id,
                                  dimensionName: phName,
                                  pointId: phase.id,
                                  pointName: phase.name,
                                })
                              );
                            }}
                            onClick={() => {
                              if (shouldIgnoreSelectionClick()) return;
                              if (!phaseDim) return;
                              setSelected(
                                rowSelected
                                  ? null
                                  : {
                                    type: "phasePoint",
                                    dimId: phaseDim.id,
                                    id: phase.id,
                                    name: phase.name,
                                  }
                              );
                              // Activity-Tracking
                              if (projectId) notifyProjectActivity(projectId).catch(() => { });
                            }}
                          >
                            {phase.name}
                          </th>

                          {/* Zellen für jede Kombination aus weekdayPoint x phasePoint */}
                          {displayWdPoints.map((weekday) => {
                            const cellKey = `${weekday.name}|${phase.name}`;
                            const isSelected =
                              selected?.type === "cell" &&
                              selected.weekday === weekday.name &&
                              (!phaseDim || selected.phase === phase.name);
                            const isVisible = visibleCells.has(cellKey);

                            // Alle Zeilen, die zu dieser Zelle gehören
                            const matching = filteredRows.filter(
                              (r) =>
                                r[weekdayDim.name] === weekday.name &&
                                (!phaseDim || r[phaseDim.name] === phase.name)
                            );

                            // Hintergrundfarbe für die Zelle, falls genau eine Stunde vorhanden ist
                            const bg =
                              isVisible && matching.length === 1
                                ? getLessonBg(data, matching[0])
                                : undefined;

                            // Event-Handler für Long-Press
                            const handlePointerDown = (e: React.PointerEvent) => {
                              // Linksklick / Touch only
                              if (e.button !== 0) return;

                              const lastOpen = longPressTimerByKey.current['__lastOpen'] || 0;
                              if (Date.now() - lastOpen < 400) return;
                              
                              const lastUp = longPressTimerByKey.current['__lastUp'] || 0;
                              if (Date.now() - lastUp < 400) return;

                              isLongPressTriggered.current = false;

                              // ggf. alten Timer für diese Zelle killen
                              const existing = longPressTimerByKey.current[cellKey];
                              if (existing) window.clearTimeout(existing);

                              const el = e.currentTarget as HTMLElement;
                              longPressTimerByKey.current[cellKey] = window.setTimeout(() => {
                                if (matching.length > 0) {
                                  const rect = el.getBoundingClientRect();
                                  setCellListOpenByKey({ [cellKey]: true });
                                  setCellListOpenMethod({ [cellKey]: 'longpress' });
                                  setOpenNormalViewDropdownKey(cellKey);
                                  setNormalViewPortalRect({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 220) });
                                  setNormalViewPortalRows(matching);
                                  longPressTimerByKey.current['__lastOpen'] = Date.now();
                                  isLongPressTriggered.current = true;
                                }
                              }, 500); // 500ms für Long Press
                            };

                            const handlePointerUp = () => {
                              longPressTimerByKey.current['__lastUp'] = Date.now();
                              setTimeout(() => { isLongPressTriggered.current = false; }, 500);

                              const t = longPressTimerByKey.current[cellKey];
                              if (t) window.clearTimeout(t);
                              longPressTimerByKey.current[cellKey] = null;
                            };

                            const handlePointerLeave = () => {
                              const t = longPressTimerByKey.current[cellKey];
                              if (t) window.clearTimeout(t);
                              longPressTimerByKey.current[cellKey] = null;
                            };

                            // Hover-Unterstützung: Liste öffnen, wenn Maus länger auf Zelle bleibt
                            const handleMouseEnter = (e: React.MouseEvent) => {
                              if (document.body.classList.contains("is-dragging")) return;
                              if (matching.length > 0) {
                                const existing = hoverTimerByKey.current[cellKey];
                                if (existing) window.clearTimeout(existing);

                                const el = e.currentTarget as HTMLElement;
                                hoverTimerByKey.current[cellKey] = window.setTimeout(() => {
                                  hoverTimerByKey.current[cellKey] = null;
                                  if (document.body.classList.contains("is-dragging")) return;
                                  const rect = el.getBoundingClientRect();
                                  setCellListOpenByKey({ [cellKey]: true });
                                  setCellListOpenMethod({ [cellKey]: 'hover' });
                                  setOpenNormalViewDropdownKey(cellKey);
                                  setNormalViewPortalRect({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 220) });
                                  setNormalViewPortalRows(matching);
                                }, 2000); // 2000ms für Hover
                              }
                            };

                            const handleMouseLeave = (e: React.MouseEvent) => {
                              // Hover-Timer abbrechen
                              const t = hoverTimerByKey.current[cellKey];
                              if (t) window.clearTimeout(t);
                              hoverTimerByKey.current[cellKey] = null;

                              // Nur bei Hover-Modus automatisch schließen
                              if (cellListOpenMethod[cellKey] !== 'hover') {
                                return; // Long-Press bleibt offen
                              }

                              // Prüfen ob Maus zum Portal-Dropdown gewandert ist
                              const relatedTarget = e.relatedTarget;
                              if (relatedTarget instanceof Element && relatedTarget.closest('.cell-dropdown-list')) {
                                return; // Maus ist zur Portal-Dropdown-Liste gewandert, Liste offen lassen
                              }

                              // Maus hat Zelle verlassen, Liste schließen (nur bei Hover)
                              setCellListOpenByKey((prev) => ({ ...prev, [cellKey]: false }));
                              setCellListOpenMethod((prev) => {
                                const { [cellKey]: _removed, ...rest } = prev;
                                return rest;
                              });
                              setOpenNormalViewDropdownKey(null);
                              setNormalViewPortalRect(null);
                            };

                            return (

                              <td
                                key={weekday.id}
                                onPointerDown={handlePointerDown}
                                onPointerUp={handlePointerUp}
                                onPointerCancel={handlePointerUp}
                                onPointerLeave={handlePointerLeave}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                onContextMenu={(e) => {
                                  // Kontextmenü unterbinden, damit der Long-Press nicht das Browser-Menü öffnet
                                  // (zumindest auf Mobile oft Standardverhalten bei Long-Press)
                                  // Wir machen es nur bedingt, falls gewünscht. Hier mal defensiv:
                                  // e.preventDefault(); 
                                  // (User hat nichts dazu gesagt, aber ist meist besser für App-Feeling)
                                }}
                                onClick={(e) => {
                                  // Wenn gerade ein Long-Press stattfand, Click ignorieren
                                  if (isLongPressTriggered.current) {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    isLongPressTriggered.current = false;
                                    return;
                                  }

                                  // Wenn Liste offen, soll ein Klick auf die Zelle selbst (nicht Dropdown) die Liste schließen?
                                  if (cellListOpenByKey[cellKey]) {
                                    setCellListOpenByKey((prev) => ({ ...prev, [cellKey]: false }));
                                    setOpenNormalViewDropdownKey(null);
                                    setNormalViewPortalRect(null);
                                    return;
                                  }

                                  // Wenn in dieser Zelle mehrere Einträge liegen: Pager "öffnen"
                                  // (vorher sah man nur die Anzahl, jetzt kann man blättern)
                                  const key = `${weekday.name}|${phase.name}`;
                                  if (matching.length > 1) {
                                    setCellPagerOpenByKey((prev) => ({ ...prev, [key]: true }));
                                    setCellEntryIndexByKey((prev) => ({ ...prev, [key]: prev[key] ?? 0 }));
                                  }

                                  setSelected(
                                    isSelected
                                      ? null
                                      : {
                                        type: "cell",
                                        weekdayDimId: weekdayDim.id,
                                        phaseDimId: phaseDim?.id || "",
                                        weekday: weekday.name,
                                        phase: phase.name,
                                      }
                                  );
                                  // Activity-Tracking
                                  if (projectId) notifyProjectActivity(projectId).catch(() => { });
                                }}
                              >
                                <div
                                  className={
                                    "lesson-cell" + (isSelected ? " selected" : "")
                                  }
                                  data-pager-open={
                                    weekdayDim
                                      ? String(!!cellPagerOpenByKey[`${weekday.name}|${phase.name}`])
                                      : "false"
                                  }
                                  style={{
                                    // Übergabe der Hintergrundfarbe an CSS (CSS Custom Property)
                                    "--lesson-bg": bg,
                                  } as React.CSSProperties}
                                >
                                  {isVisible &&
                                    getCellContent(weekday.name, phase.name, matching)}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                )
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ── Normal-Ansicht Dropdown Portal – rendert direkt in document.body ── */}
      {openNormalViewDropdownKey && normalViewPortalRect && normalViewPortalRows.length > 0 && (() => {
        const key = openNormalViewDropdownKey;
        const { top, left, width } = normalViewPortalRect;
        const listWidth = Math.min(Math.max(width, 220), window.innerWidth - 16);
                const clampedLeft = Math.min(
          Math.max(left, 8),
          window.innerWidth - listWidth - 8
        );
        const spaceBelow = window.innerHeight - top;
        const spaceAbove = top - 8;
        const useUpward = spaceBelow < 260 && spaceAbove > spaceBelow;
        const maxHeight = Math.min(320, Math.max(160, useUpward ? spaceAbove - 8 : spaceBelow - 8));
        const dimsToShow = otherDims.filter((d: any) => {
          if (selected?.type === "point" && d.id === selected.dimId) return false;
          if (superFilterEnabledByDimId[d.id]) return false;
          return true;
        });

        return ReactDOM.createPortal(
          <div
            key={key}
            className={`cell-dropdown-list ${useUpward ? 'open-upward' : ''}`}
            style={{
              position: "fixed",
              top: useUpward ? undefined : top,
              bottom: useUpward ? (window.innerHeight - top + 8) : undefined,
              width: listWidth,
              left: clampedLeft,
              minWidth: 220,
              maxWidth: "min(500px, calc(100vw - 16px))",
              maxHeight,
              overflowY: "auto",
              zIndex: 9999,
            }}
            onClick={(e: any) => {
              e.stopPropagation();
              if (isLongPressTriggered.current) {
                isLongPressTriggered.current = false;
              }
            }}
            onPointerDown={(e: any) => e.stopPropagation()}
            onMouseEnter={() => {
              // Maus ist auf dem Portal – Hover-Timer stoppen
              const t = hoverTimerByKey.current[key];
              if (t) window.clearTimeout(t);
              hoverTimerByKey.current[key] = null;
            }}
            onMouseLeave={() => {
              if (cellListOpenMethod[key] === 'hover') {
                setCellListOpenByKey((prev: any) => ({ ...prev, [key]: false }));
                setCellListOpenMethod((prev: any) => { const { [key]: _r, ...rest } = prev; return rest; });
                setOpenNormalViewDropdownKey(null);
                setNormalViewPortalRect(null);
              }
            }}
          >
            {normalViewPortalRows.map((mRow: any, idx: number) => {
              const label = dimsToShow.map((d: any) => mRow[d.name]).filter(Boolean).join(" | ");
              const isCurrent = (cellEntryIndexByKey[key] ?? 0) === idx;
              return (
                <div
                  key={idx}
                  className={`cell-dropdown-item ${isCurrent ? "selected" : ""}`}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    if (isLongPressTriggered.current) {
                      isLongPressTriggered.current = false;
                      return;
                    }
                    setCellEntryIndexByKey((prev: any) => ({ ...prev, [key]: idx }));
                    setCellPagerOpenByKey((prev: any) => ({ ...prev, [key]: true }));
                    setCellListOpenByKey((prev: any) => ({ ...prev, [key]: false }));
                    setOpenNormalViewDropdownKey(null);
                    setNormalViewPortalRect(null);
                  }}
                >
                  {label || <span className="multi-count">1</span>}
                </div>
              );
            })}
          </div>,
          document.body
        );
      })()}

      {/* ── Kalender Overflow Portal – rendert direkt in document.body ── */}
      {openCalendarOverflowGroupId && calendarOverflowPortalRect && timeSlotLayout && (() => {
        const group = timeSlotLayout.overflowGroups.find((g: any) => g.id === openCalendarOverflowGroupId);
        if (!group) return null;
        const { top, left, width } = calendarOverflowPortalRect;
        const listWidth = Math.min(Math.max(width, 280), window.innerWidth - 16);        
        const clampedLeft = Math.min(
          Math.max(left, 8),
          window.innerWidth - listWidth - 8
        );

        const spaceBelow = window.innerHeight - top;
        const spaceAbove = top - 8;
        const useUpward = spaceBelow < 260 && spaceAbove > spaceBelow;
        const maxHeight = Math.min(320, Math.max(160, useUpward ? spaceAbove - 8 : spaceBelow - 8));        
        return ReactDOM.createPortal(
          <div
            key={`overflow-${group.id}`}
            className={`cell-dropdown-list ${useUpward ? 'open-upward' : ''}`}
            style={{
              position: "fixed",
              top: useUpward ? undefined : top,
              bottom: useUpward ? (window.innerHeight - top + 8) : undefined,
              width: listWidth,
              left: clampedLeft,
              minWidth: 280,
              maxWidth: "min(500px, calc(100vw - 24px))",
              maxHeight,
              overflowY: "auto",
              zIndex: 9999,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isLongPressTriggered.current) {
                isLongPressTriggered.current = false;
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseEnter={() => {
              // Maus ist auf dem Portal – Liste offen halten
              setOpenCalendarOverflowGroupId(openCalendarOverflowGroupId);
            }}
            onMouseLeave={() => {
              setOpenCalendarOverflowGroupId(null);
              setCalendarOverflowPortalRect(null);
            }}
          >
            {group.segments
              .slice()
              .sort((a: any, b: any) => {
                const byStart = a.startMin - b.startMin;
                return byStart !== 0 ? byStart : a.laneIndex - b.laneIndex;
              })
              .map((segment: any) => {
                const isCurrent = selected?.type === "calendarEvent" && selected.eventId === segment.eventId;
                const displayValues = getRowDisplayValues(segment.row);
                const label = displayValues.join(" | ") || segment.phaseName;
                const timeLabel = `${formatMinutes(segment.startMin)} – ${formatMinutes(segment.endMin)}`;
                return (
                  <div
                    key={`portal-${group.id}-${segment.id}`}
                    className={`cell-dropdown-item${isCurrent ? " selected" : ""}`}
                    onClick={() => {
                      if (isLongPressTriggered.current) {
                        isLongPressTriggered.current = false;
                        return;
                      }
                      setSelected({
                        type: "calendarEvent",
                        eventId: segment.eventId,
                        rowIndex: segment.row.__rowIndex ?? segment.eventId,
                        weekdayDimId: weekdayDim!.id,
                        phaseDimId: phaseDim!.id,
                        weekday: displayWdPoints[group.columnIndex]?.name ?? "",
                        phase: segment.phaseName,
                      });
                      setOpenCalendarOverflowGroupId(null);
                      setCalendarOverflowPortalRect(null);
                      if (projectId) notifyProjectActivity(projectId).catch(() => { });
                    }}
                  >
                    {timeLabel} | {label}
                  </div>
                );
              })}
          </div>,
          document.body
        );
      })()}
    </>
  );
}
